<template>
  <div class="wizard">
    <div class="navbar p-0">
      <div class="navbar-container">
        <ul class="nav nav-pills step-pills row mx-0 px-1">
          <wizard-step
            v-for="item in steps"
            :key="item.stepNumber"
            :step-number="item.stepNumber"
            :number-desc="item.numberDesc"
            :step-desc="item.stepDesc"
            :active="item.active"
            :completed="item.completed"
            :show-step="item.showStep"
          />
        </ul>
      </div>
    </div>

    <div class="clearfix" />

    <section id="section-tabs" class="w-100">
      <div class="tab-content card">
        <div class="tab-pane active">
          <div class="tab-pane-header">
            <div class="tag-badge">
              <span class="dot text-center py-1">
                <i class="fas fa-user-circle orange-icon-define" />
              </span>
              <span class="tag-badge-title py-1 pl-2">Definir PEI o PS</span>
            </div>
            <div class="label-current">
              <b>Paso {{ currentStep.numberDesc }}:</b>
              {{ currentStep.stepDesc }}
            </div>
          </div>
          <div class="tab-pane-body">
            <div v-if="!isCurrentStepValid" id="errors-1">
              <template v-for="message in messagesWrapper">
                <div
                  v-if="
                    message.show &&
                      (!message.stepNumber ||
                        message.stepNumber == currentStep.stepNumber)
                  "
                  :key="message.key"
                  class="alert alert-danger custom-red-alert"
                  role="alert"
                  :class="message.type"
                >
                  <a class="close" data-dismiss="alert">×</a>
                  <i class="fa fa-times-circle" aria-hidden="true" />
                  &nbsp;
                  <span v-html="message.text" />
                </div>
              </template>
            </div>

            <question-group
              v-for="step in steps"
              v-show="step.stepNumber == currentStepIndex"
              :key="step.stepNumber"
              :ref="'questionGroup_' + step.stepNumber"
              :questions="step.questions"
              :step-type="step.stepType"
              :step-number="step.stepNumber"
              :feature-icon="step.featureIcon"
              :confirmation-message="step.confirmationMessage"
              :confirmation-title="step.confirmationTitle"
              :is-disabled="isReadOnly"
              @onContinueClick="continueClicked"
              @statusChange="onStepStatusChange"
            />

            <div
              v-if="
                currentStepIndex == 2 && showElegibilityDeterminationConditions
              "
            >
              <pei-eligibility-determination-conditions
                v-model="peiEligibilityDeterminationData"
                ref="peiEligibilityDeterminationConditionsRef"
                :questionNumber="getQuestionNumberOfEligibilityConditions"
                :conditions="determinationElegibilityOptions"
                :disabled="isReadOnly"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
    <div>
      <div class="action-button-container">
        <div class="d-flex justify-content-end mr-5">
          <div class="row mt-3 action-button-container__action">
            <div class="col">
              <ActionButton
                variant="danger"
                class="action-button-custom"
                i="fa fa-times-circle"
                @click="cancelClicked"
              >
                Cancelar
              </ActionButton>
            </div>

            <div
              v-if="
                this.currentStepIndex > 1 &&
                  !IsShortForm &&
                  this.canGoToPreviousStep
              "
              class="col"
            >
              <ActionButton
                variant="outline-success"
                class="action-button-custom"
                i="far fa-arrow-circle-left"
                @click="backClicked"
              >
                Regresar
              </ActionButton>
            </div>

            <div class="col">
              <button
                type="button"
                class="btn btn-success action-button-custom"
                @click.stop.prevent="btnContinueClick"
                :disabled="isContinueDisabled"
              >
                <i class="mr-2 far fa-arrow-circle-right "></i>
                Continuar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import WizardStep from './WizardStep';
import QuestionGroup from './StepQuestionsGroup';
import router from '@/router/index';
import mixAlert from '@/mixin/alert.mixin';
import mixWait from '@/mixin/loading.mixin';
import Swal from 'sweetalert2';
import EduEspYesNoNA from '@/utils/constants/EduEspYesNoNA';
import months from '@/utils/constants/months';
import asistantServiceOptions from '@/utils/constants/asistantServiceOptions';
import peiTypes from '@/utils/constants/pei/peiTypes';
import peiStatuses from '@/utils/constants/pei/peiStatus';
import schoolGrades from '@/utils/constants/schoolGrades';
import peiTransitionAges from '@/utils/constants/pei/peiTransitionAges';
import isNil from 'lodash/isNil';
import peiService from '@/services/pei/general';
import ActionButton from '@/components/common/ActionButton';
import ObjectToFormData from 'object-to-formdata';
import store from '@/store/index';
import PeiEligibilityDeterminationConditions from './PeiEligibilityDeterminationConditions.vue';
import studentTrianualService from '@/modules/student-trianual/api.js';
import {
  getSysparameterByNames,
  getSysparameterByNameAndAcademicYear,
} from '@/modules/system-parameter-managment/api';
import { hasInterventionsDue } from '@/modules/compensatory-service/api';
import schoolCenters from '@/utils/constants/student/schoolCenters.js';
import moment from 'moment';

const programTypes = Object.freeze({ PEI: 1, PEI_Unilateral: 2, PS: 3 });
const validationResultTypes = Object.freeze({ Good: 0, Error: 1, Warning: 2 });
const messageTypes = Object.freeze({
  Warning: 'alert-warning',
  Error: 'alert-danger',
  Info: 'alert-info',
  Success: 'alert-success',
});
// const validationMessages = ["R03200050"];
const StudentStatuses = Object.freeze({
  PreRegistro: 125,
  Registro: 126,
  Evaluado: 127,
  Servido: 128,
  NoElegible: 129,
  Egreso: 131,
  Disponible: 200,
  Inactivo: 219,
  Elegible: 303,
});

const SchoolTypes = Object.freeze({
  Institutions: 349,
});

const PeiAdditionalQuestionType = Object.freeze({
  Evidencia_de_Participación_de_Alternatia_de_Ubicación: 1,
  Evidencia_de_Participación_Servicio_Transición_Post_Secundario: 2,
  Evidencia_de_que_la_Agencia_Proveyó_el_Servicio: 3,
  Fecha_de_Ubicación: 4,
  Asistente_de_Servicio: 5,
  Asistente_de_Servicio_Único: 6,
  Evidencia_movilidad_acceso: 7,
  Necesidades_expresadas: 8,
  Ajuste_organizacion_escolar: 9,
  Proveyó_ajuste: 10,
  Tipo_ajuste: 11,
  Ajuste_ambiente_integrado: 12,
  Ajuste_en_termino_5_dias: 13,
  Necesidad_Movilidad: 14,
  Condiciones_Adicionales_Durante_El_Proceso_Determinacion_Elegibilidad: 15,
});

const PREGUNTA_SEIS = '6';
const PREGUNTA_SIETE = '7';
const PREGUNTA_OCHO = '8';
const PREGUNTA_NUEVE = '9';
const PREGUNTA_DIEZ = '10';
const PREGUNTA_ONCE = '11';
const PREGUNTA_TRECE = '13';
const PREGUNTA_CATORCE = '14';

const PREGUNTA_P_SIETE = 'P7';
const SECTION_ANSWER = 1;
const SECTION_ANSWER_VII = 6;

const SUBSECTION_C = 27;
const SUBSECTION_D = 28;

const AGE_LIMIT = 22.0;

export default {
  name: 'PEIDefinitionWizard',
  mixins: [mixAlert, mixWait],
  props: {
    studentBirthDate: Date,
  },
  components: {
    QuestionGroup,
    WizardStep,
    ActionButton,
    PeiEligibilityDeterminationConditions,
  },
  data() {
    //Dummy object con la ifo del estudiante
    let Student = {
      impedimento: 'Otros Impedimentos de Salud', //proviene de la info del estudiante
      age: 7, //proviene de la info del estudiante
      matriculaActiva: false, //proviene de la info del estudiante
      NumberPeiPs: 0, // Numero de PEI o PS,
      HasPeioPSTothisAcademciYearSelected: false, // Tienee PEI o PS Firmado para el anos academico
      hasSignedPeiLastYear: false,
      ElegibilityInitialDate: new Date(2019, 10, 1),
      studentSie: 20951734,
    };

    return {
      loading: false,
      latestSignedPei: null,
      showPeiQuestion18: false,
      activePscCreation: false,
      brailleTeaching: false,
      currentStepIndex: 1,
      orderQuestion: 1,
      messages: [],
      isCurrentStepValid: true,
      steps: [
        {
          stepNumber: 1,
          numberDesc: '1',
          stepDesc: 'Año Académico',
          active: true,
          completed: false,
          isValid: true,
          showStep: true,
          questions: [
            {
              number: 1,
              displayNumber: '1',
              description:
                'Seleccione el año académico en el cual implementará el PEI o PS',
              answerControlType: 0,
              getOptions: this.getAcademicYears,
              onChange: this.onQuestion1ValueChange,
              isRequired: true,
              requireErrorCode: '3',
              requireErrorMessage: '',
              initialState: true,
            },
            {
              number: 2,
              displayNumber: '2',
              description: '¿Este PEI o PS se considera para el año?',
              answerControlType: 1,
              isRequired: true,
              requireErrorCode: '2',
              requireErrorMessage: '',
              onChange: this.onQuestion2ValueChange,
              initialState: true,
              isVisible: false,
            },
            {
              number: 2.05,
              displayNumber: '2',
              subNumber: '.0',
              description: '¿Desea crear el plan de servicios compensatorios?',
              answerControlType: 1,
              isRequired: true,
              requireErrorCode: '7',
              requireErrorMessage: '',
              onChange: this.onQuestion2dot0ValueChange,
              initialState: true,
              isVisible: false,
            },
            {
              number: 2.1,
              displayNumber: '2',
              subNumber: '.1',
              description:
                '¿Desea crear una copia de los datos ingresados para el PEI o PS?',
              answerControlType: 1,
              isVisible: false,
              isRequired: true,
              requireErrorCode: '',
              requireErrorMessage: 'Favor de seleccionar Sí o No.',
              initialState: false,
            },
          ],
        },
        {
          stepNumber: 2,
          numberDesc: 'P',
          stepDesc: 'Preguntas Adicionales',
          showStep: false,
          validatePreviousYear: true,
          active: false,
          completed: false,
          isValid: true,
          questions: [
            {
              number: 1,
              displayNumber: 'P1',
              isDisabled: false,
              description:
                'Según se seleccionó en el PEI o PS del año escolar ' +
                (this.academicYear - 1) +
                ' la alternativa de ubicación recomendada fue ' +
                '[Aquí debe presentar la alternativa de ubicación seleccionada en el último PEI o PS en estatus firmado], ' +
                ' ¿se tiene evidencia de que el estudiante participó de esta alternativa de ubicación? ',
              answerControlType: 1,
              isRequired: true,
              requireErrorCode: '190604',
              requireErrorMessage: '',
              onChange: this.onQuestion2Step1ValueChange,
              initialState: true,
            },
            {
              number: 1.1,
              displayNumber: 'P1',
              isDisabled: false,
              subNumber: '.1',
              description:
                'Seleccione la alternativa de ubicación en la que el estudiante participó',
              answerControlType: 0,
              getOptions: this.getAlternateLocation,
              isRequired: true,
              isVisible: false,
              requireErrorCode: '190605',
              requireErrorMessage: '',
              initialState: false,
            },
            {
              number: 1.2,
              displayNumber: 'P1',
              isDisabled: false,
              subNumber: '.2',
              description:
                'Justifique la razón por la cual el estudiante no participó de la alternativa de ubicación recomendada. En caso de que la causa esté relacionada con un cambio en la alternativa de ubicación, deberá explicar por qué no se enmendó el PEI para atender la necesidad identificada.',
              answerControlType: 3,
              isVisible: false,
              isRequired: true,
              requireErrorCode: '',
              requireErrorMessage: 'Introduzca una justificación',
              initialState: false,
              fields: [
                {
                  id: 'comentario',
                  type: 'textarea',
                  label: '<b>Comentario:</b>',
                  name: 'question-comment',
                  class: 'col-lg-12 col-sm-6',
                  maxChars: 3000,
                  value: null,
                  required: true,
                  requireErrorCode: '',
                  requireErrorMessage: 'Por favor introduzca texto',
                },
              ],
            },
            {
              number: 2,
              displayNumber: 'P2',
              isDisabled: false,
              description:
                '<p>En la Parte V del PEI <b>' +
                this.academicYear +
                '</b> el estudiante tiene un programa de servicios con actividades dirigidas al proceso de transición postsecundario</p>',
              answerControlType: 1,
              isVisible: false,
              onChange: this.onQuestion2Step2ValueChange,
              isRequired: true,
              requireErrorCode: '190606',
              requireErrorMessage: '',
              initialState: false,
            },
            {
              number: 2.1,
              displayNumber: 'P2',
              isDisabled: false,
              subNumber: '.1',
              description:
                'Favor de explicar las razones por la cual el estudiante no participó de las actividades dirigidas al proceso de transición postsecundario',
              answerControlType: 3,
              isVisible: false,
              isRequired: true,
              requireErrorCode: '190607',
              requireErrorMessage: '',
              initialState: false,
              fields: [
                {
                  id: 'comentario',
                  type: 'textarea',
                  label: '<b>Comentario:</b>',
                  name: 'question-comment',
                  class: 'col-lg-12 col-sm-6',
                  maxChars: 3000,
                  value: null,
                  required: true,
                  requireErrorCode: '',
                  requireErrorMessage: 'Por favor introduzca texto',
                },
              ],
            },
            {
              number: 3,
              displayNumber: 'P3',
              isDisabled: false,
              description:
                'En la Parte IX del PEI [Año Académico del último PEI o PS en estatus firmado] se estableció la necesidad de invitar a otras agencias para proveer algún servicio, ¿existe evidencia de que la agencia proveyó el servicio?',
              answerControlType: 1,
              isVisible: false,
              isRequired: true,
              requireErrorCode: '190608',
              requireErrorMessage: '',
              onChange: this.onQuestion3ValueChange,
              initialState: false,
            },
            {
              number: 3.1,
              displayNumber: 'P3',
              isDisabled: false,
              subNumber: '.1',
              description:
                'Especifique todas aquellas gestiones que se realizaron para que la agencia proveyera el servicio.',
              answerControlType: 3,
              isRequired: true,
              isVisible: false,
              requireErrorCode: '190609',
              requireErrorMessage: '',
              initialState: false,
            },
            {
              number: 4,
              displayNumber: 'P4',
              isDisabled: false,
              description:
                '¿El estudiante actualmente tiene el servicio de Asistente de Servicios?',
              answerControlType: 1,
              isRequired: true,
              isVisible: true,
              requireErrorCode: '190725',
              requireErrorMessage: '',
              onChange: this.onQuestion4ValueChange,
              initialState: true,
            },
            {
              number: 4.1,
              displayNumber: 'P4',
              isDisabled: false,
              subNumber: '.1',
              description:
                '¿Las tareas de este Asistente de Servicio están dirigidas a atender a un solo estudiante?',
              answerControlType: 1,
              isRequired: true,
              requireErrorCode: '190722',
              requireErrorMessage: '',
              onChange: this.onQuestion4AValueChange,
              isVisible: false,
              initialState: false,
            },
            {
              number: 4.2,
              displayNumber: 'P4',
              isDisabled: false,
              subNumber: '.2',
              description: 'El servicio de Asistente es para: ',
              answerControlType: 0,
              getOptions: this.getAsistantServiceOptions,
              isRequired: true,
              requireErrorCode: '190724',
              requireErrorMessage: '',
              isVisible: false,
              initialState: false,
            },
            {
              number: 4.3,
              displayNumber: 'P4',
              isDisabled: false,
              subNumber: '.3',
              description: 'Nombre del Asistente',
              answerControlType: 2,
              isRequired: true,
              requireErrorCode: '190723',
              requireErrorMessage: '',
              isVisible: false,
              initialState: false,
            },
            {
              number: 5,
              displayNumber: 'P5',
              isDisabled: false,
              description:
                'El estudiante está en estatus Elegible. Favor de seleccionar la fecha de ubicación inicial.',
              answerControlType: 4,
              isRequired: true,
              requireErrorMessage:
                'Favor de elegir fecha de ubicación inicial. ',
              pickerMinDate: this.pickerMinDate,
              isVisible: this.studentIsElegible,
              initialState: this.studentIsElegible,
            },
            {
              number: 6,
              displayNumber: 'P6',
              isDisabled: false,
              description:
                'Según se seleccionó en el PEI o PS del año escolar anterior (anterior) el estudiante tení­a problemas de movilidad y acceso, el estudiante ¿Presentó alguna necesidad de acceso en la escuela?',
              answerControlType: 1,
              isRequired: true,
              requireErrorCode: '',
              requireErrorMessage: 'Favor de seleccionar Sí o No.',
              onChange: this.onQuestion6ValueChange,
              isVisible: false,
              initialState: false,
              fields: [
                {
                  id: 'comentario',
                  type: 'textarea',
                  label: '<b>Comentario:</b>',
                  name: 'question-comment',
                  class: 'col-lg-12 col-sm-6',
                  maxChars: 3000,
                  value: null,
                  required: true,
                  requireErrorCode: '',
                  requireErrorMessage: 'Por favor introduzca texto',
                  showWithValue: false,
                },
                {
                  id: 'options',
                  label:
                    '(Puede marcar más de una opción del siguiente catálogo):',
                  type: 'multiple-selection-options',
                  name: 'question-comment',
                  class: 'col-lg-12 col-sm-6',
                  value: null,
                  options: [],
                  getOptions: this.getProblemasMovilidad,
                  selectedOptions: [],
                  required: true,
                  requireErrorCode: '',
                  requireErrorMessage: 'Por favor seleccione una opción',
                  showWithValue: true,
                },
              ],
            },
            {
              number: 6.1,
              displayNumber: 'P6',
              isDisabled: false,
              subNumber: '.1',
              description:
                '¿Estas necesidades fueron expresadas en el PEI ' +
                this.academicYear +
                '?',
              answerControlType: 1,
              isRequired: true,
              requireErrorCode: '',
              requireErrorMessage: 'Favor de seleccionar Sí o No.',
              isVisible: false,
              initialState: false,
              onChange: this.onQuestion6AValueChange,
              notes: [
                {
                  message:
                    'Estas necesidades son expresadas en la Parte I(C)- Comentarios de salud, Parte III,(B) – Descripción de discapacidad, Parte VI(J) – Modificaciones al programa',
                },
              ],
              fields: [
                {
                  id: 'comentario',
                  type: 'textarea',
                  label: 'Explique:',
                  name: 'question-comment',
                  class: 'col-lg-12 col-sm-6',
                  maxChars: 3000,
                  value: null,
                  required: true,
                  requireErrorCode: '',
                  requireErrorMessage: 'Por favor introduzca texto',
                  showWithValue: false,
                },
              ],
            },
            {
              number: 6.7,
              displayNumber: 'P6',
              isDisabled: false,
              subNumber: '.2',
              description: '¿Fue atendida la necesidad de movilidad?',
              answerControlType: 1,
              isRequired: true,
              requireErrorCode: '',
              requireErrorMessage: 'Favor de seleccionar Sí o No.',
              isVisible: false,
              initialState: false,
              onChange: this.onQuestion6GValueChange,
              fields: [
                {
                  id: 'comentario',
                  type: 'textarea',
                  label: 'Explique:',
                  name: 'question-comment',
                  class: 'col-lg-12 col-sm-6',
                  maxChars: 3000,
                  value: null,
                  required: true,
                  requireErrorCode: '',
                  requireErrorMessage: 'Por favor introduzca texto',
                  showWithValue: false,
                },
              ],
            },
            {
              number: 6.2,
              displayNumber: 'P6',
              isDisabled: false,
              subNumber: '.3',
              description:
                '¿Requirió un ajuste a la organización escolar para atender la necesidad de movilidad y/o acceso?',
              answerControlType: 1,
              isRequired: true,
              requireErrorCode: '',
              requireErrorMessage: 'Favor de seleccionar Sí o No.',
              isVisible: false,
              initialState: false,
              onChange: this.onQuestion6BValueChange,
              fields: [
                {
                  id: 'comentario',
                  type: 'textarea',
                  label: 'Explique:',
                  name: 'question-comment',
                  class: 'col-lg-12 col-sm-6',
                  maxChars: 3000,
                  value: null,
                  required: true,
                  requireErrorCode: '',
                  requireErrorMessage: 'Por favor introduzca texto',
                  showWithValue: false,
                },
              ],
            },

            {
              number: 6.3,
              displayNumber: 'P6',
              isDisabled: false,
              subNumber: '.4',
              description: '¿Se le proveyó el ajuste?',
              answerControlType: 1,
              isRequired: true,
              requireErrorCode: '',
              requireErrorMessage: 'Favor de seleccionar Sí o No.',
              isVisible: false,
              initialState: false,
              onChange: this.onQuestion6CValueChange,
              fields: [
                {
                  id: 'comentario',
                  type: 'textarea',
                  label: 'Explique:',
                  name: 'question-comment',
                  class: 'col-lg-12 col-sm-6',
                  maxChars: 3000,
                  value: null,
                  required: true,
                  requireErrorCode: '',
                  requireErrorMessage: 'Por favor introduzca texto',
                  showWithValue: false,
                },
              ],
            },
            {
              number: 6.4,
              displayNumber: 'P6',
              isDisabled: false,
              subNumber: '.5',
              description: '¿Qué tipo de ajuste se realizó?',
              answerControlType: 7,
              isRequired: true,
              requireErrorCode: '',
              requireErrorMessage:
                'Por favor seleccione una opción y/o introduzca texto',
              isVisible: false,
              initialState: false,
              fields: [
                {
                  id: 'tipoajustes',
                  type: 'multiple-selection-options',
                  label: '(selección múltiple)',
                  name: 'tipo-ajustes',
                  class: 'col-lg-12 col-sm-6',
                  value: null,
                  options: [],
                  getOptions: this.getTiposdeAjustesMovilidad,
                  selectedOptions: [],
                  required: true,
                  requireErrorCode: '',
                  requireErrorMessage: 'Por favor seleccione una opción',
                  onChange: this.onQuestion6dot4FieldAjustesChange,
                },
                {
                  id: 'comentario',
                  type: 'textarea',
                  label: 'Explique:',
                  name: 'question-comment',
                  class: 'col-lg-12 col-sm-6',
                  maxChars: 3000,
                  value: null,
                  required: true,
                  requireErrorCode: '',
                  requireErrorMessage: 'Por favor introduzca texto',
                  visible: false,
                },
              ],
            },
            {
              number: 6.5,
              displayNumber: 'P6',
              isDisabled: false,
              subNumber: '.6',
              description:
                '¿El ajuste se proveyó en ambiente integrado y apropiado de acuerdo a sus necesidades?',
              answerControlType: 1,
              isRequired: true,
              requireErrorCode: '',
              requireErrorMessage: 'Por favor seleccione una opción',
              isVisible: false,
              initialState: false,
              onChange: this.onQuestion6eValueChange,
            },
            {
              number: 6.6,
              displayNumber: 'P6',
              isDisabled: false,
              subNumber: '.7',
              description:
                '¿El ajuste fue realizado en un término de 5 dí­as o menos a partir de la identificación de la necesidad?',
              answerControlType: 1,
              isRequired: true,
              requireErrorCode: '',
              requireErrorMessage: 'Favor de seleccionar Sí o No.',
              isVisible: false,
              initialState: false,
              onChange: this.onQuestion6FValueChange,
              fields: [
                {
                  id: 'comentario',
                  type: 'textarea',
                  label: 'Explique:',
                  name: 'question-comment',
                  class: 'col-lg-12 col-sm-6',
                  maxChars: 3000,
                  value: null,
                  required: true,
                  requireErrorCode: '',
                  requireErrorMessage: 'Por favor introduzca texto',
                  showWithValue: false,
                },
              ],
            },
            {
              number: 7,
              displayNumber: 'P7',
              isDisabled: false,
              description:
                'Con el propósito de recopilar datos estadísticos sobre condiciones en el Programa de Educación Especial, ¿se identificaron condiciones adicionales durante el proceso de revisión del expediente del estudiante? En el caso de los estudiantes con autismo debe identificar el Nivel según la evaluación del estudiante. En caso de que la evaluación no especifique el nivel, identifique bajo la categoría No especificado',
              answerControlType: 1,
              isRequired: true,
              requireErrorCode: '',
              requireErrorMessage:
                'Favor de contestar si se identificaron condiciones adicionales',
              isVisible: true,
              initialState: true,
              onChange: this.onQuestionP7ValueChange,
            },
          ],
        },
        {
          stepNumber: 3,
          numberDesc: '2',
          stepDesc: 'Inicio',
          active: false,
          completed: false,
          isValid: true,
          showStep: true,
          questions: [
            {
              number: 7,
              displayNumber: '3',
              isDisabled: false,
              description:
                '¿<b>' +
                this.impedimentDescription +
                '</b>  es el impedimento por el cual el estudiante ha sido determinado elegible?',
              answerControlType: 1,
              isRequired: true,
              requireErrorCode: '',
              requireErrorMessage: 'Favor de seleccionar Sí o No.',
              onChange: this.onQuestion7ValueChange,
              isVisible: true,
              initialState: true,
            },
            {
              number: 8,
              displayNumber: '4',
              isDisabled: false,
              description:
                '¿El estudiante tiene la edad pertinente para cursar un grado preescolar?',
              answerControlType: 1,
              isRequired: true,
              requireErrorCode: '',
              requireErrorMessage: 'Favor de seleccionar Sí o No.',
              onChange: this.onQuestion8ValueChange,
              isVisible: true,
              initialState: true,
            },
            {
              number: 9,
              displayNumber: '5',
              isDisabled: false,
              description:
                '¿Se recomienda que el estudiante esté asignado a una escuela del DE para la implementación del PEI?',
              answerControlType: 1,
              isRequired: true,
              requireErrorCode: '',
              requireErrorMessage: 'Favor de seleccionar Sí o No.',
              warningMessage: 'Warning test',
              onChange: this.onQuestion9ValueChange,
              isVisible: true,
              initialState: true,
            },
            {
              number: 9.1,
              displayNumber: '5',
              isDisabled: false,
              subNumber: '.1',
              description:
                '¿Se recomienda que el estudiante esté ubicado en un Head Start, Hogar o Centro de Cuido?',
              answerControlType: 1,
              isVisible: false,
              initialState: false,
              isRequired: true,
              requireErrorCode: '',
              requireErrorMessage: 'Favor de seleccionar Sí o No.',
              onChange: this.onQuestion9dot1ValueChange,
            },
            {
              number: 9.2,
              displayNumber: '5',
              isDisabled: false,
              subNumber: '.2',
              description:
                '¿Se recomienda que el estudiante esté ubicado en una institución educativa?',
              answerControlType: 1,
              isRequired: true,
              requireErrorCode: '',
              requireErrorMessage: 'Favor de seleccionar Sí o No.',
              onChange: this.onQuestion9dot2ValueChange,
              isVisible: false,
              initialState: false,
            },
            /* #94485 Define PEI: Eliminar Pregunta de Propuesta Corta
            Se eliminan las preguntas 9.3 y 9.4 del paso 3 del proceso para cumplir con  los requerimientos de ley.
            */
          ],
        },
        {
          stepNumber: 4,
          numberDesc: '3',
          stepDesc: 'Detalle',
          active: false,
          completed: false,
          showStep: true,
          isValid: true,
          questions: [
            {
              number: 5.1,
              displayNumber: '6',
              isDisabled: false,
              description:
                'Favor de seleccionar la ubicación del estudiante recomendada para la implementación de este PEI o PS',
              answerControlType: 7,
              isRequired: true,
              requireErrorCode: '',
              requireErrorMessage: '',
              isVisible: true,
              initialState: true,
              fields: [
                {
                  id: 'region',
                  type: 'dropdown',
                  label: '<b>Región:</b>',
                  name: 'question-comment',
                  class: 'col-lg-4 col-sm-6 mb question-dropdown',
                  maxChars: 100,
                  fieldkey: 'Value',
                  fieldname: 'Display',
                  onChange: this.onRegionChange,
                  options: [],
                  getOptions: this.getRegions,
                  value: null,
                  required: true,
                  requireErrorCode: '15',
                  requireErrorMessage: '',
                },
                {
                  id: 'distrito',
                  type: 'dropdown',
                  label: '<b>Distrito:</b>',
                  name: 'question-comment',
                  class: 'col-lg-4 col-sm-6 mb question-dropdown',
                  maxChars: 100,
                  fieldkey: 'Value',
                  fieldname: 'Display',
                  options: [],
                  onChange: this.onDistrictChange,
                  value: null,
                  required: true,
                  requireErrorCode: '17',
                  requireErrorMessage: '',
                },
                {
                  id: 'escuela',
                  type: 'dropdown',
                  label: '<b>Escuela:</b>',
                  name: 'question-comment',
                  class: 'col-lg-4 col-sm-6 mb question-dropdown',
                  maxChars: 100,
                  fieldkey: 'Value',
                  fieldname: 'Display',
                  onChange: this.onSchoolChange,
                  options: [],
                  value: null,
                  required: true,
                  requireErrorCode: '19',
                  requireErrorMessage: '',
                },
              ],
            },
            {
              number: 9,
              displayNumber: '7',
              isDisabled: false,
              description:
                '¿El estudiante tiene un impedimento cognoscitivo significativo?',
              notes: [
                {
                  message:
                    'Al marcar <b>Sí</b> en esta pregunta, será requerido que documente un servicio educativo de Modalidad de Salón Especial y tipo de Enfoque Ruta 3.',
                },
                {
                  message:
                    '<b>SI</b> = En la evaluación psicométrica se evidencia que su funcionamiento cognitivo es significativamente bajo y/o muestra más de 6 años o más de rezago al compararse con estudiantes de su misma edad.',
                },
                {
                  message:
                    '<b>No</b> = En la evaluación psicométrica se evidencia que su funcionamiento cognitivo es promedio o moderado al ser comparado con estudiantes de su misma edad.',
                },
              ],
              answerControlType: 1,
              isRequired: true,
              requireErrorCode: '',
              requireErrorMessage: 'Favor de seleccionar Sí o No.',
              isVisible: true,
              initialState: true,
              onChange: this.onQuestion9Step4ValueChange, // this.updateEvaluationProgram,
            },
            {
              number: 6,
              displayNumber: '8',
              isDisabled: false,
              description: 'Grado en el que recibirá acceso curricular ',
              answerControlType: 0,
              isRequired: true,
              requireErrorCode: '',
              requireErrorMessage: 'Favor de seleccionar un grado.',
              getOptions: this.getGrados,
              onChange: this.onQuestion6Step4ValueChange,
              isVisible: false,
              initialState: true,
            },
            {
              number: 7,
              displayNumber: '9',
              isDisabled: false,
              description: '¿El estudiante muestra conducta inapropiada?',
              answerControlType: 1,
              isRequired: true,
              requireErrorCode: '',
              onChange: this.onQuestion7Step4ValueChange,
              requireErrorMessage: 'Favor de seleccionar Sí o No.',
              isVisible: true,
              initialState: true,
            },
            {
              number: 7.1,
              displayNumber: '9',
              isDisabled: false,
              description: '¿El estudiante tendrá servicios relacionados?',
              answerControlType: 1,
              isRequired: true,
              requireErrorCode: '',
              requireErrorMessage: 'Favor de seleccionar Sí o No.',
              isVisible: this.isFromPeiDetail,
              initialState: false,
            },
            {
              number: 8,
              displayNumber: '10',
              isDisabled: false,
              description: '¿El estudiante tendrá servicios de transición?',
              answerControlType: 1,
              isRequired: true,
              requireErrorCode: '',
              requireErrorMessage: 'Favor de seleccionar Sí o No.',
              onChange: this.onQuestion8Step4ValueChange,
              isVisible: true,
              initialState: true,
            },
            {
              number: 8.1,
              displayNumber: '10',
              isDisabled: false,
              subNumber: '.1',
              description: 'Seleccione el tipo de transición.',
              answerControlType: 0,
              isRequired: true,
              requireErrorCode: '',
              requireErrorMessage:
                'Favor de seleccionar el tipo de transición.',
              getOptions: this.getTransitions,
              isVisible: false,
              initialState: false,
            },
            {
              number: 10,
              displayNumber: '11',
              isDisabled: false,
              description: '¿El estudiante necesitará de acomodos?',
              answerControlType: 1,
              isRequired: true,
              requireErrorCode: '',
              requireErrorMessage: 'Favor de seleccionar Sí o No.',
              isVisible: true,
              initialState: true,
            },
            {
              number: 11,
              displayNumber: '12',
              isDisabled: false,
              description:
                'Programa de Medición por el cual se evaluará el progreso académico y funcional del estudiante',
              notes: [
                {
                  message:
                    '<b>Alterna</b> = Los estudiantes elegibles para medición alterna deben presentar impedimento cognoscitivo significativo.',
                },
                {
                  message:
                    '<b>N/A</b> = Estudiantes que reciben acceso al grado de K, 1ro, 2do, 9no, 10mo, 12mo y adultos.',
                },
                {
                  message:
                    '<b>Regular</b> = Todos los demás estudiantes de los grados 3, 4, 5, 6, 7, 8 y 11.',
                },
              ],
              answerControlType: 0,
              getOptions: this.getMeasurePrograms,
              isRequired: true,
              onChange: this.onQuestion11Step4ValueChange,
              requireErrorCode: '',
              requireErrorMessage:
                'Favor de seleccionar el programa de medición',
              isVisible: true,
              initialState: true,
            },
            {
              number: 11.1,
              displayNumber: '12',
              isDisabled: false,
              subNumber: '.1',
              description: '¿El Programa de Medición será con Acomodo?',
              answerControlType: 1,
              isRequired: true,
              isVisible: false,
              initialState: false,
              requireErrorCode: '',
              requireErrorMessage: 'Favor de seleccionar Sí o No.',
            },
            {
              number: 12,
              displayNumber: '13',
              isDisabled: false,
              description:
                '¿Requiere el estudiante Servicios y Equipos de Asistencia Tecnológica?',
              answerControlType: 1,
              isRequired: true,
              requireErrorCode: '',
              requireErrorMessage: 'Favor de seleccionar Sí o No.',
              isVisible: true,
              initialState: true,
              onChange: this.onQuestion12Step4ValueChange,
            },
            {
              number: 13,
              displayNumber: '14',
              isDisabled: false,
              description: '¿Recomienda servicios de año escolar extendido?',
              answerControlType: 1,
              isRequired: true,
              requireErrorCode: '',
              requireErrorMessage: 'Favor de seleccionar Sí o No.',
              isVisible: true,
              initialState: true,
            },
            {
              number: 14,
              displayNumber: '15',
              isDisabled: false,
              description:
                '¿El estudiante presenta un impedimento fí­sico que limita su movilidad y acceso?',
              notes: [
                {
                  message:
                    '<b>Sí</b> = Estudiante que presenta una limitación en su ambulación de forma temporal o permanente que requiere de alguna gestión para garantizar su acceso a la educación de forma equitativa.',
                },
                {
                  message:
                    '<b>No</b> = Estudiante que no presenta una necesidad de movilidad. ',
                },
              ],
              answerControlType: 1,
              isRequired: true,
              requireErrorCode: '',
              requireErrorMessage: 'Favor de seleccionar Sí o No.',
              isVisible: true,
              initialState: true,
            },
            {
              number: 15,
              displayNumber: '16',
              isDisabled: false,
              description:
                '¿El estudiante requiere enseñanza en lenguaje de señas?',
              notes: [
                {
                  message:
                    '<b>Sí</b> = Estudiante que, por su impedimento auditivo, presenta una dificultad severa para comunicarse a través del lenguaje hablado por lo que requiere de aprendizaje en lenguaje de señas y/o adiestramiento en labio lectura.',
                },
                {
                  message:
                    '<b>No</b> = Estudiante que no presenta problemas auditivos, estudiante que presenta problemas auditivos pero se comunica a través del lenguaje hablado.',
                },
              ],
              answerControlType: 1,
              isRequired: true,
              requireErrorCode: '',
              requireErrorMessage: 'Favor de seleccionar Sí o No.',
              isVisible: true,
              initialState: true,
              onChange: this.onQuestion15Step4ValueChange,
            },
            {
              number: 19,
              displayNumber: '16.1',
              isDisabled: false,
              description:
                '¿Cual es el nivel de enseñanza de lenguaje de señas?',
              answerControlType: 0,
              fieldkey: 'Value',
              fieldname: 'Display',
              getOptions: this.getSignLanguageProficiencyLevels,
              isRequired: true,
              requireErrorCode: 'R90056',
              requireErrorMessage: '',
              isVisible: false,
              initialState: false,
            },
            {
              number: 16,
              displayNumber: '17',
              isDisabled: false,
              description: '¿El estudiante requiere enseñanza en Braille?',
              notes: [
                {
                  message:
                    '<b>Sí</b> = Estudiante cuenta con estos 3 criterios: <br />(1) tiene agudeza visual de 20/200 o menos en el mejor ojo con corrección y/o presenta una limitación en el campo visual, donde el ancho del diámetro esté opuesto a una distancia angular que no sea mayor de 20 grados, <br />(2) tiene una recomendación médica que estipula que tiene o tendrá un deterioro visual progresivo y <br />(3) muestra un funcionamiento cognitivo promedio o promedio bajo.',
                },
                {
                  message:
                    '<b>No</b> = Estudiante que presenta una agudeza visual de 20/100 o mayor en el mejor ojo con corrección y/o presenta una limitación menor de 30 grados, su condición NO es progresiva y presenta impedimentos cognoscitivos moderados o significativos.',
                },
              ],
              answerControlType: 1,
              isRequired: true,
              requireErrorCode: '',
              requireErrorMessage: 'Favor de seleccionar Sí o No.',
              isVisible: true,
              initialState: true,
              onChange: this.onQuestion16Step4ValueChange,
            },
            {
              number: 17,
              displayNumber: '17.1',
              isDisabled: false,
              description: '¿Cuál es su medio de Lectura?',
              answerControlType: 7,
              isRequired: true,
              requireErrorCode: '',
              isVisible: false,
              initialState: false,
              fields: [
                {
                  id: 'Primario',
                  type: 'dropdown',
                  label: '<b>Primario:</b>',
                  name: 'question-comment',
                  class: 'col-lg-4 col-sm-6 mb question-dropdown',
                  maxChars: 100,
                  fieldkey: 'Value',
                  fieldname: 'Display',
                  options: [],
                  getOptions: this.codeReadingMode,

                  value: null,
                  required: true,
                  requireErrorCode: 'R90052',
                  requireErrorMessage: '',
                  initialState: true,
                },
                {
                  id: 'Secundario',
                  type: 'dropdown',
                  label: '<b>Secundario:</b>',
                  name: 'question-comment',
                  class: 'col-lg-4 col-sm-6 mb question-dropdown',
                  maxChars: 100,
                  fieldkey: 'Value',
                  fieldname: 'Display',
                  onChange: this.onSchoolChange,
                  options: [],
                  getOptions: this.codeReadingMode,
                  value: null,
                  required: true,
                  requireErrorCode: '19',
                  requireErrorMessage: '',
                  initialState: true,
                },
              ],
            },
            {
              number: 18,
              displayNumber: '17.2',
              isDisabled: false,
              description: '¿Cuál es el Nivel de Lectura?',
              answerControlType: 0,
              fieldkey: 'Value',
              fieldname: 'Display',
              getOptions: this.codeReadingLevel,
              isRequired: true,
              requireErrorCode: 'R90053',
              requireErrorMessage: '',
              isVisible: false,
              initialState: false,
            },
            {
              number: 20,
              displayNumber: '18',
              isDisabled: false,
              description:
                '¿El estudiante estará ubicado en un salón especial modificado (SEM)?',
              answerControlType: 1,
              isRequired: true,
              requireErrorCode: '',
              requireErrorMessage: 'Favor de seleccionar Sí o No.',
              isVisible: false,
              initialState: true,
            },
          ],
        },
        {
          stepNumber: 5,
          numberDesc: '4',
          stepDesc: 'Creación',
          stepType: 'message',
          featureIcon: 'far fa-check-circle',
          confirmationTitle:
            'El PEI se comenzará a crear con la información detallada en las pantallas anteriores.',
          confirmationMessage: '¿Está seguro que desea continuar?',
          active: false,
          completed: false,
          showStep: true,
          isValid: true,
        },
      ],
      recommendedLocation: '',
      academicYear: 0,
      revisionType: '',
      programType: programTypes.PEI,
      peiDefinition: {
        academicYearId: 0,
      },
      academicYears: null,
      validationMessages: [],
      Student: Student,
      impedimento: 'Otros Impedimentos de Salud', //proviene de la info del estudiante
      matriculaActiva: false, //proviene de la info del estudiante
      signedPeiId: 0, // Numero de PEI o PS,
      hasPeioPSTothisAcademciYearSelected: false, // Tienee PEI o PS Firmado para el anos academico
      hasService: false, //
      RealAcademicYear: 0,
      studentConfAgeGrade: 0,
      PeiGeneralDB: {},
      PeiGeneral: {
        PeiId: '',
        StudentSieId: 0,
        PeiTypeCode: 0,
        ConfigAgeAmt: 0,
        ConfigAcademicYearId: 0,
        IsRevision: null,
        IsUnilateral: null,
        //propiedades paso adicional
        PeiAdditionalQuestions: [],
        //propiedades paso inicio
        ConfigImpedimentFlag: null,
        ConfigPrescholarFlag: null,
        ConfigAgeAmtFlag: null,
        ConfigPublicSchoolFlag: null,
        IsSpecialPrivateStudent: null,
        IsInstitutional: null,
        ConfigUnilateralFlag: null,
        ConfigLocationRejectFlag: null,
        LocationRejectDocument: null,
        //propidedades paso detalle
        ConfigLocationRegionId: null,
        ConfigLocationDistrictId: null,
        PeiLocationId: null,
        RegionId: null,
        DistrictId: null,
        ConfigSchoolId: null,
        GeneralStudentPublicSchoolFlag: null,
        ConfigGradeId: null,
        ConfigInapropriateConductFlag: null,
        ConfigTransitionServicesFlag: null,
        PeiTransitionCode: null,
        ConfigPlacementFlag: null,
        PeiOtherServMeasureCode: null,
        ConfigOtherServMeasurePlacementFlag: null,
        ConfigOtherServTechAssistEquipFlag: null,
        ConfigExtendedSchoolYearFlag: null,
        ConfigMobilityProblemFlag: null,
        ConfigSignsLanguageFlag: null,
        ConfigBrailleFlag: null,
        PeiUnilateralLocationId: null,
        ConfigUnilateralServiceEducationalFlag: null,
        ConfigUnilateralServiceRelatedFlag: null,
        PlacedInModifiedClassroomFlag: null,
        ConfigImpedimentCognativeFlag: null,
        GeneralSchoolDistrictId: null,
        GeneralSchoolRegionId: null,
        GeneralSchoolId: null,
      },
      PeiAdditionalQuestion: {
        PeiAdditionalQuestionId: null,
        PeiAdditionalQuestionAnswerId: null,
        AdditionalQuestionComment: null,
        AlternatePlacementId: null,
        AlternatePlacementDate: null,
        ServiceAssistantName: null,
        ServiceAssistantServiceId: null,
      },
      SelectedPeiId: null,
      SelectedPeiYear: null,
      SelectedPeiYearId: null,
      SelectedPeiYearText: null,
      isFromTab1: false,
      IsShortForm: false,
      studentIsElegible: false,
      PickerMinDate: new Date(),
      impedimentDescription: '',
      oldEPKValue: false,
      showElegibilityDeterminationConditions: false,
      determinationElegibilityOptions: [],
      peiEligibilityDeterminationData: {
        eligibilityDeterminationConditions: {
          condition: null,
          selectedItems: [],
        },
      },
      canUseNewLogic: false,
      studentAgeByAcademicYear: null,
      studentAgeAtAcademicYearEnd: null,
      sysParameterCache: {},
    };
  },
  computed: {
    isContinueDisabled() {
      return this.loading;
    },
    hasEditGradePermission() {
      return store.getters.HasPermission('pei.detail.gradequestion');
    },
    canCreatePscWithoutSchool() {
      return store.getters.HasPermission(
        'academic.pei.psc.create-withoutschool'
      );
    },
    isPS() {
      return this.$store.state.pei.configUnilateralFlag == '1';
    },
    isPSC() {
      return this.$store.state.pei.configUnilateralFlag == '2';
    },
    currentStep() {
      return this.steps[this.currentStepIndex - 1];
    },
    nextStep() {
      return this.steps[this.currentStepIndex];
    },
    studentSieId() {
      return this.$store.getters['$_student/SelectedStudent'].studentId;
    },
    isFromPeiDetail() {
      return this.peiId != undefined;
    },
    studentAge() {
      return this.$store.getters['$_student/getMipeStudent'].age;
    },
    hasPscCreationPermission() {
      return this.userHasPermissions('academic.pei.psc.create');
    },
    canCreatePSC() {
      return (
        this.activePscCreation && (this.isPscRequired || this.isPscOptional)
      );
    },
    isPscRequired() {
      return (
        this.studentAgeByAcademicYear >= AGE_LIMIT &&
        this.revisionType == 'El primero'
      );
    },
    isPscOptional() {
      if (this.isPscRequired) return false;
      const hasGrade = [schoolGrades.EEA, schoolGrades.Twelve].includes(
        this.latestSignedPei?.configGradeId
      );
      return (
        this.studentAgeByAcademicYear < AGE_LIMIT &&
        (hasGrade ||
          this.userHasPermissions(
            'academic.pei.showCreateCompensatoryServicesPlanQuestion'
          ))
      );
    },
    isReadOnly() {
      return (
        this.PeiGeneralDB.peiStatusCode == peiStatuses.Completado ||
        this.PeiGeneralDB.peiStatusCode == peiStatuses.Firmado
      );
    },
    peiId() {
      return this.$route.params.peiId;
    },
    canGoToPreviousStep() {
      if (!this.peiId) return true;
      return this.currentStepIndex > 2;
    },
    isInvalidStep3() {
      const questions5 = [
        this.getQuestionElement(3, 9),
        this.getQuestionElement(3, 9.1),
        this.getQuestionElement(3, 9.2),
      ];
      return !questions5.some(
        (question) => isNil(question.selectedAnswer) || question.selectedAnswer
      );
    },
    getQuestionNumberOfEligibilityConditions() {
      let element = this.getQuestionElement(2, 7);

      return element.displayNumber;
    },
    messagesWrapper() {
      return [
        {
          text: 'Favor de validar y/o contestar las preguntas.',
          type: messageTypes.Error,
          show: true,
        },
        {
          text:
            'Si desea realizar un Plan de Servicio, debe proceder con la redacción de una Propuesta de PEI y rechazar la misma en la hoja de cotejo.',
          type: messageTypes.Error,
          show: this.isInvalidStep3,
          stepNumber: 3,
        },
        ...this.messages,
      ];
    },
    isInInstitution() {
      let schoolType = this.$store.getters['$_student/getMipeStudent']
        .mipeStudent.schoolType;
      return schoolType == SchoolTypes.Institutions ? true : false;
    },
    hasLocationSchoolStudent() {
      return ![
        schoolCenters.PublicCenter,
        schoolCenters.PrivateCenter,
        schoolCenters.NoAvailable,
      ].includes(
        this.$store.getters['$_student/getMipeStudent'].mipeStudent.schoolId
      );
    },
    amendment() {
      return this.$store.state['$_admendment'] ?? {};
    },
    amendmentSections() {
      return this.amendment.sections ?? [];
    },
    canEnableSectionByAmendant() {
      return this.amendmentSections.some((x) => x.sectionId == SECTION_ANSWER);
    },
    canEnableSectionVIIByAmendant() {
      return this.$store.state['$_admendment'].sections.some(
        (x) => x.sectionId == SECTION_ANSWER_VII
      );
    },
    canEnableSectionBySubsectionGSectionVIAmendant() {
      const subSectionG = 39;
      return this.$store.state['$_admendment'].sections.some(
        (x) => x.subSectionId == subSectionG
      );
    },
    canEnableSectionBySubsectionFSectionVIAmendant() {
      const subSectionF = 38;
      return this.$store.state['$_admendment'].sections.some(
        (x) => x.subSectionId == subSectionF
      );
    },
    canEnableSectionBySubsectionKSectionVIAmendant() {
      const subSectionK = 50;
      return this.$store.state['$_admendment'].sections.some(
        (x) => x.subSectionId == subSectionK
      );
    },
    canEnableSectionBySubsectionASectionVIAmendant() {
      const subSectionA = 33;
      return this.$store.state['$_admendment'].sections.some(
        (x) => x.subSectionId == subSectionA
      );
    },
    admendmentIsEmpty() {
      return this.amendmentSections.length == 0;
    },
    canShowSectionEditableBySubcsectionFSectionVI() {
      return (
        this.canEnableSectionBySubsectionFSectionVIAmendant &&
        this.canUseNewLogic &&
        !this.admendmentIsEmpty
      );
    },
    canShowSectionEditableBySubcsectionGSectionVI() {
      return (
        this.canEnableSectionBySubsectionGSectionVIAmendant &&
        this.canUseNewLogic &&
        !this.admendmentIsEmpty
      );
    },
    canShowSectionEditableBySubcsectionKSectionVI() {
      return (
        this.canEnableSectionBySubsectionKSectionVIAmendant &&
        this.canUseNewLogic &&
        !this.admendmentIsEmpty
      );
    },
    canShowSectionEditableBySubcsectionASectionVI() {
      return (
        this.canEnableSectionBySubsectionASectionVIAmendant &&
        this.canUseNewLogic &&
        !this.admendmentIsEmpty
      );
    },
    canShowSectionVIIEditable() {
      return (
        this.canEnableSectionVIIByAmendant &&
        this.canUseNewLogic &&
        !this.admendmentIsEmpty
      );
    },
    canShowSectionEditable() {
      return (
        this.canEnableSectionByAmendant &&
        this.canUseNewLogic &&
        !this.admendmentIsEmpty
      );
    },
    hasSubSectionCSelectedInAmendant() {
      return this.amendmentSections.some((x) => x.subSectionId == SUBSECTION_C);
    },
    hasSubSectionDSelectedInAmendant() {
      return this.amendmentSections.some((x) => x.subSectionId == SUBSECTION_D);
    },
    sectionCSelectedAndParam() {
      return (
        this.hasSubSectionCSelectedInAmendant &&
        this.canUseNewLogic &&
        !this.admendmentIsEmpty
      );
    },
    sectionDSelectedAndParam() {
      return (
        this.hasSubSectionDSelectedInAmendant &&
        this.canUseNewLogic &&
        !this.admendmentIsEmpty
      );
    },
  },
  async mounted() {
    await Promise.all([
      this.loadValidationMessages(),
      this.getStudentInfo(),
      this.getConditionsCatalog(),
      this.getActivePSCCreationSysParam(),
    ]);

    this.studentIsElegible = this.isElegible();
    this.pickerMinDate = this.formatDateForPicker(
      new Date(this.studentInfo.mipeStudent.actualEligibilityDate)
    );
    this.impedimentDescription = this.studentInfo.mipeStudent.impedimentDescription;
    this.getQuestionElement(3, 7).text =
      '¿<b>' +
      this.impedimentDescription +
      '</b>  es el impedimento por el cual el estudiante ha sido determinado elegible?';

    this.getQuestionElement(4, 11).disabled = true;
    this.getQuestionElement(4, 11).isVisible = !this.isPS;

    if (this.isFromPeiDetail) {
      this.getStep(5).confirmationTitle = this.peiId
        ? 'Ir al detalle del PEI o PS.'
        : 'El PEI o PS será modificado con la información detallada en las pantallas anteriores.';

      this.SelectedPeiId = this.peiId;
      await this.getPeiYear();
      await this.getPei();

      if (this.PeiGeneralDB.unilateralFlag != '1') {
        this.isFromTab1 = true;
        await this.mapPeiAnswersToQuestions();
        await this.getStep2AnswerFromEdit();
      } else {
        await this.mapPSAnswersToQuestions();
      }
      await this.toPeiDetail();
      await this.enableSections();
    }
  },

  methods: {
    async getActivePSCCreationSysParam() {
      const { data } = await getSysparameterByNames({
        parameterNames: ['ActivePSCCreation'],
      });

      this.activePscCreation =
        data
          ?.find((x) => x.name == 'ActivePSCCreation')
          ?.staticValue?.toLowerCase() == 'true' ?? false;
    },
    async getShowQuestion18SysParameter() {
      const academicYear = !this.isFromPeiDetail
        ? this.getQuestionElement(1, 1).selectedAnswer
        : this.SelectedPeiYear
        ? this.SelectedPeiYear
        : this.GetCurrentYear();

      // added this cache beacuse it calls this method multiple times before the step 3 and it's called again in the step 3.
      const { data } =
        this.sysParameterCache[`ShowPeiQuestion18:${academicYear}`] ??
        (await getSysparameterByNameAndAcademicYear(
          'ShowPeiQuestion18',
          academicYear
        ));

      this.sysParameterCache[`ShowPeiQuestion18:${academicYear}`] = { data };

      if (data && typeof data === 'object' && 'value' in data) {
        this.showPeiQuestion18 = data.value?.toLowerCase() == 'true' ?? false;
      } else {
        this.showPeiQuestion18 = false;
      }
    },
    // US148120 once the start date is in effect, it must allow selecting Yes or No
    // otherwise, it must disable the question and set the selected answer to No
    async applyExtendedSchoolYearStartDateRule() {
      const question = this.getQuestionElement(4, 13);

      if (!question) return;

      if (
        this.PeiGeneralDB.peiStatusCode > 0 &&
        this.PeiGeneralDB.peiStatusCode != peiStatuses.Borrador
      ) {
        return;
      }

      const academicYear = !this.isFromPeiDetail
        ? this.getQuestionElement(1, 1).selectedAnswer
        : this.SelectedPeiYear
        ? this.SelectedPeiYear
        : this.GetCurrentYear();

      const cacheKey = `EnableExtendedSchoolYearStartDate:${academicYear}`;
      const { data } =
        this.sysParameterCache[cacheKey] ??
        (await getSysparameterByNameAndAcademicYear(
          'EnableExtendedSchoolYearStartDate',
          academicYear
        ));
      this.sysParameterCache[cacheKey] = { data };

      const parameterDateRaw =
        data?.value ?? data?.staticValue ?? data?.Value ?? null;
      if (
        parameterDateRaw == null ||
        parameterDateRaw == undefined ||
        parameterDateRaw == ''
      )
        return;
      const parsedParameterDate = moment(
        String(parameterDateRaw).trim(),
        [
          'YYYY/MM/DD',
          'YYYY/MM/DDTHH:mm:ss',
          'YYYY/MM/DDTHH:mm:ss.SSSZ',
          'DD/MM/YYYY',
          'D/M/YYYY',
        ],
        true
      );
      if (!parsedParameterDate.isValid()) return;
      const parameterDate = parsedParameterDate.startOf('day').toDate();

      const today = moment()
        .startOf('day')
        .toDate();

      // US148120 once the start date is in effect, it must allow selecting Yes or No
      if (today >= parameterDate) {
        return;
      }

      // US148142 if the user has the permission, it must allow selecting Yes or No
      if (
        this.$store.getters.HasPermission(
          'academic.pei.peidefinition.canselectextendedschoolyear'
        )
      ) {
        return;
      }

      // otherwise, it must disable the question and set the selected answer to No
      question.selectedAnswer = false;
      question.isDisabled = true;
    },
    async loadQuestion18() {
      await this.getShowQuestion18SysParameter();
      this.setQuestionVisible(4, 20, this.showPeiQuestion18);
      if (this.showPeiQuestion18) {
        const question = this.getQuestionElement(4, 20);
        if (question) {
          question.selectedAnswer = this.PeiGeneralDB.placedInModifiedClassroomFlag;
        }
      }
    },
    async enableSections() {
      const correlationParameters = this.amendment?.correlationParameter ?? [];
      this.canUseNewLogic =
        correlationParameters.length > 0 &&
        correlationParameters[0]?.staticValue === 'true';
      const excludeQuestion = [PREGUNTA_P_SIETE]; //aqui podemos colocar preguntas del define PEI  que pueden llegar sin respuestas y ser requerida
      this.steps.forEach((item) => {
        if (item.questions) {
          item.questions.forEach((question) => {
            if (
              !this.admendmentIsEmpty &&
              !excludeQuestion.some((q) => q == question.displayNumber) &&
              this.canUseNewLogic
            ) {
              question.isDisabled = true;
            }
            if (
              this.canShowSectionEditable &&
              (question.displayNumber == PREGUNTA_SEIS ||
                question.displayNumber == PREGUNTA_OCHO)
            ) {
              question.isDisabled = false;
              question.isRequired = false;
            }
            if (
              this.sectionCSelectedAndParam &&
              question.displayNumber == PREGUNTA_NUEVE
            ) {
              question.isDisabled = false;
              question.isRequired = false;
            }
            if (
              this.canShowSectionEditableBySubcsectionASectionVI &&
              question.displayNumber == PREGUNTA_OCHO
            ) {
              question.isDisabled = false;
              question.isRequired = false;
            }
            if (
              this.sectionDSelectedAndParam &&
              (question.displayNumber == PREGUNTA_OCHO ||
                question.displayNumber == PREGUNTA_DIEZ)
            ) {
              question.isDisabled = false;
              question.isRequired = false;
            }
            if (
              this.canShowSectionEditableBySubcsectionFSectionVI &&
              question.displayNumber == PREGUNTA_ONCE
            ) {
              question.isDisabled = false;
              question.isRequired = false;
            }

            if (
              this.canShowSectionEditableBySubcsectionGSectionVI &&
              (question.displayNumber == PREGUNTA_SIETE ||
                question.displayNumber == PREGUNTA_OCHO)
            ) {
              question.isDisabled = false;
              question.isRequired = false;
            }

            if (
              this.canShowSectionEditableBySubcsectionKSectionVI &&
              question.displayNumber == PREGUNTA_TRECE
            ) {
              question.isDisabled = false;
              question.isRequired = false;
            }

            if (
              this.canShowSectionVIIEditable &&
              question.displayNumber == PREGUNTA_CATORCE
            ) {
              question.isDisabled = false;
              question.isRequired = false;
            }
          });
        }
      });
      this.addValidationMessage();
    },
    addValidationMessage() {
      let question = this.getQuestionElement(4, 12);
      question.validationResult.resultType = validationResultTypes.Warning;
      question.validationResult.messageText =
        'Al seleccionar que no requiere equipo de Asistencia Tecnológica estos serán removidos del PEI y se le notificará al CAAT a través de la garantía Inventario de Equipos AT SAEE-AT-2016-8 que estos serán devueltos.';
    },
    async getStudentAgeByAcademicYear() {
      let selectedAnswerYearId = this.getQuestionElement(1, 1).selectedAnswerId;
      const parameters = {
        studentSie: this.$store.getters.SelectedStudent.studentId,
        academicYearId: selectedAnswerYearId,
      };
      this.studentAgeAtAcademicYearEnd = null;
      const [response, endResponse] = await Promise.all([
        this.$ApiGet(
          '/api/student/getstudentageinacademicyeardatefrom',
          parameters
        ),
        this.$ApiGet(
          '/api/student/getstudentageinacademicyeardateto',
          parameters
        ),
      ]);

      if (response && response.data)
        this.studentAgeByAcademicYear = response.data;
      this.studentAgeAtAcademicYearEnd = endResponse?.data ?? null;
      return this.studentAgeByAcademicYear;
    },
    async getStudentConfigGradeByAcademicYear() {
      let selectedAnswerYearId = this.getQuestionElement(1, 1).selectedAnswerId;

      let { data } = await this.$ApiGet(
        '/api/student/confagegradeacademicyear',
        {
          studentSie: this.$store.getters.SelectedStudent.studentId,
          academicYearId: selectedAnswerYearId,
        }
      );
      return data;
    },
    async getStudentInfo() {
      let self = this;
      await self
        .$ApiGet(
          '/api/student/getstudentdetails?studentSie=' + this.studentSieId,
          null,
          true
        )
        .then((res) => {
          self.studentInfo = res.data;
        })
        .catch((err) => {
          self.showErrorMessage(err);
        });
    },
    async getStep2AnswerFromEdit() {
      let peiId = this.studentInfo.mipeStudent.peiId;
      let result = await this.getRecommendedLocation(peiId);
      this.recommendedLocation = result && result.description;
      let question1Step2Element = this.getQuestionElement(2, 1);
      question1Step2Element.text =
        '<p>Según se seleccionó en el PEI o PS del año escolar <b>' +
        (this.RealAcademicYear - 2) +
        '-' +
        (this.RealAcademicYear - 1) +
        '</b> la alternativa de ubicación recomendada fue <b>' +
        this.recommendedLocation +
        '</b>, ' +
        ' ¿se tiene evidencia de que el estudiante participó de esta alternativa de ubicación? </p>';
    },
    async getPei() {
      let self = this;
      await self
        .$ApiGet('/api/pei/getpei?peiId=' + this.SelectedPeiId, null, true)
        .then((res) => {
          self.PeiGeneralDB = res.data;
          if (self.PeiGeneralDB.generalClosedDate) {
            self.PeiGeneralDB.generalClosedDate = new Date(
              self.model.generalClosedDate
            );
          }
          if (
            self.PeiGeneralDB.unilateralFlag == '1' &&
            self.PeiGeneralDB.peiStatusCode == peiStatuses.Borrador
          ) {
            self.IsShortForm = true;
          }
        })
        .catch((err) => {
          self.response = err;
        });
    },
    async onRegionChange(item) {
      if (item && item.Value) {
        let academicyear = !this.isFromPeiDetail
          ? this.getQuestionElement(1, 1).selectedAnswer
          : this.SelectedPeiYear
          ? this.SelectedPeiYear
          : this.GetCurrentYear();
        let questionElement = this.getQuestionElement(4, 5.1);
        let distritoField = questionElement.getField('distrito');
        let escuelaField = questionElement.getField('escuela');
        distritoField.value = null;
        escuelaField.value = null;
        distritoField.options = await this.getDistricts(
          academicyear,
          item.Value
        );
      }
    },
    async onDistrictChange(item) {
      if (item && item.Value) {
        let questionElement = this.getQuestionElement(4, 5.1);
        let escuelaField = questionElement.getField('escuela');

        let isPublic = this.getQuestionElement(3, 9).selectedAnswer;
        let isSpecialSchool = this.getQuestionElement(3, 9.1).selectedAnswer;
        let isInstitution = this.getQuestionElement(3, 9.2).selectedAnswer;
        let selectedAnswerYearId = this.getQuestionElement(1, 1)
          .selectedAnswerId;
        let isUnilateral = this.$store.state.pei.configUnilateralFlag;

        escuelaField.value = null;
        escuelaField.options = await this.getSchools(
          selectedAnswerYearId,
          item.Value,
          isUnilateral,
          isPublic,
          isSpecialSchool,
          isInstitution
        );
      }
    },
    async onSchoolChange(item) {
      this.validateJuvenilInstitution();
      //Set question 11 and 11.1 not visible if selectedSchool is C.A.S.A Project
      if (!this.isPS) {
        this.getQuestionElement(4, 11).isVisible = !item.casaFlag;
      }
      this.getQuestionElement(4, 11.1).isVisible = !item.casaFlag;
      await this.onQuestion11Step4ValueChange(this.getQuestionElement(4, 11));
      if (item && this.oldEPKValue == item.isEPK) {
        return;
      }
      if (item && item.isEPK) {
        this.oldEPKValue = item.isEPK;
        let questionGrades = this.getQuestionElement(4, 6);
        questionGrades.refreshOptions();
        questionGrades.disabled = true;
      } else {
        this.oldEPKValue = item.isEPK;
        let questionGrades = this.getQuestionElement(4, 6);
        questionGrades.refreshOptions();
        questionGrades.disabled = false;
      }
    },

    loadValidationMessages() {
      const messageCodes = [
        '2',
        '3',
        '4',
        '7',
        'R03200049',
        'R03200050',
        '190604',
        '190605',
        '190608',
        '190609',
        '190725',
        '190722',
        '190724',
        '190723',
        '22061',
        '190902',
        '190606',
        '190607',
        '15',
        '17',
        '19',
        '0737',
        '0738',
        'R03200148',
        '5543701',
        'R90052',
        'R90053',
        'R90056',
      ];
      let promise = this.$ApiGet(
        '/api/validationsmessages?messageCodes=' +
          messageCodes.join('&messageCodes=')
      );
      promise.then((result) => {
        this.validationMessages = result.data;

        //set messages for all question
        for (let stepIdx = 0; stepIdx < this.steps.length; stepIdx++) {
          const step = this.steps[stepIdx];
          if (step.questions != undefined) {
            step.questions.forEach((question) => {
              if (
                question.requireErrorCode != '' &&
                question.requireErrorCode != null &&
                question.requireErrorCode != undefined
              ) {
                question.requireErrorMessage = this.getValidationMessage(
                  question.requireErrorCode
                );
              }
              if (question.fields != null && question.fields.length > 0) {
                question.fields.forEach((item) => {
                  let questionElement = this.getQuestionElement(
                    step.stepNumber,
                    question.number
                  );
                  let field = questionElement.getField(item.id);
                  if (
                    field.requireErrorCode != '' &&
                    field.requireErrorCode != null &&
                    field.requireErrorCode != undefined
                  ) {
                    field.requireErrorMessage = this.getValidationMessage(
                      field.requireErrorCode
                    );
                  }
                });
              }
            });
          }
        }
      });
    },
    getValidationMessage(code) {
      let result = this.validationMessages.find((item) => item.id === code);
      return result.name;
    },
    showMessage(messageText, type) {
      this.messages.push({
        text: messageText,
        type: type,
      });
    },
    onStepStatusChange(stepElement) {
      let isValid = stepElement.isValid;

      if (!isValid && stepElement.stepNumber !== this.currentStepIndex) return;

      this.isCurrentStepValid = isValid;
    },
    async getAlternateLocation() {
      const result = await this.getAlternateLocations();

      let alternate = [];
      result.forEach((element) => {
        alternate.push({
          Value: element.codeNo,
          Display: element.spashortDescription,
          isSelected: false,
        });
      });

      return alternate;
    },
    updateEvaluationProgram() {
      const evaluationCodes = {
        regular: 16,
        evaluacionAlterna: 17,
        noAplica: 18,
      };
      const academicYear = this.getQuestionElement(1, 1).selectedAnswer;
      const gradeAnswer = this.getQuestionElement(4, 6).selectedAnswer;
      const impedimentAnswer = this.getQuestionElement(4, 9).selectedAnswer;
      const noApply = [1, 2, 3, 4, 11, 12, 14, 26, 28, 32, 33];
      const programMeditionQuestion = this.getQuestionElement(4, 11);
      // JSF: Accordin to US46004: for year (2023-2024) and onwards the rule will change
      if (academicYear >= 2024) {
        // 12 is 10th grade and 13 is 11th grade
        noApply[noApply.findIndex((id) => id === 12)] = 13;
        programMeditionQuestion.notes[1].message =
          '<b>N/A</b> = Estudiantes que reciben acceso al grado de K, 1ro, 2do, 9no, 11mo, 12mo y adultos.';
        programMeditionQuestion.notes[2].message =
          '<b>Regular</b> = Todos los demás estudiantes de los grados 3, 4, 5, 6, 7, 8 y 10.';
      } else {
        programMeditionQuestion.notes = [
          {
            message:
              '<b>Alterna</b> = Los estudiantes elegibles para medición alterna deben presentar impedimento cognoscitivo significativo.',
          },
          {
            message:
              '<b>N/A</b> = Estudiantes que reciben acceso al grado de K, 1ro, 2do, 9no, 10mo, 12mo y adultos.',
          },
          {
            message:
              '<b>Regular</b> = Todos los demás estudiantes de los grados 3, 4, 5, 6, 7, 8 y 11.',
          },
        ];
      }
      let evaluationProgramAnswer;
      if (noApply.indexOf(gradeAnswer) != -1 || this.isInInstitution) {
        evaluationProgramAnswer = evaluationCodes.noAplica;
      } else {
        evaluationProgramAnswer = impedimentAnswer
          ? evaluationCodes.evaluacionAlterna
          : evaluationCodes.regular;
      }

      this.getQuestionElement(4, 11).selectedAnswer = evaluationProgramAnswer;
      this.onQuestion11Step4ValueChange(this.getQuestionElement(4, 11));
    },
    getAcademicYears() {
      let promise = new Promise(async (resolve) => {
        let res = await this.$ApiGet(
          '/api/academicyears/peiquestions?showPreviousYears=true'
        );
        let academicYears = [];
        res.data.forEach((element) => {
          academicYears.push({
            Id: element.id,
            Value: element.year,
            Display: element.description,
            isSelected: false,
          });
        });
        resolve(academicYears);
      });
      return promise;
    },
    getAsistantServiceOptions() {
      return this.resolveOptions(asistantServiceOptions);
    },
    getProblemasMovilidad() {
      let options = [
        {
          value: 1,
          option:
            'Necesidad de tener acceso a la escuela (entradas a la escuela)',
        },
        {
          value: 2,
          option: 'Necesidad de tener acceso al edificio donde recibió clases',
        },
        {
          value: 3,
          option:
            'Necesidad de tener acceso al (los) salón(es) de clases donde fue asignado, salón de educación especial o área de terapias',
        },
        {
          value: 4,
          option:
            'Necesidad de tener acceso a áreas comunes (comedor, biblioteca, baños, laboratorios o el patio)',
        },
        {
          value: 5,
          option:
            'Necesidad de hacer ajustes dentro del salón de clases para facilitar su movilidad',
        },
      ];
      return this.resolveOptions(options);
    },
    getTiposdeAjustesMovilidad() {
      let options = [
        { value: 6, option: 'El grupo se movió a la planta baja ' },
        { value: 7, option: 'Se realizó un cambio de salón' },
        {
          value: 8,
          option:
            'Se ofrecieron acomodos razonables en el área de ambiente y lugar',
        },
        { value: 9, option: 'Se cambió de horario del Programa del Maestro' },
        {
          value: 10,
          option:
            'Se solicitó recursos adicionales (Ej. Asistente de servicios para movilidad, Maestro para orientación y movilidad)',
        },
        {
          value: 11,
          option: 'Se hizo una reducción de la matrí­cula en el grupo',
        },
        {
          value: 12,
          option: 'Se requirió trasladar estudiante a otra escuela',
        },
        {
          value: 13,
          option: 'Se requirió ubicar a un estudiante en el hogar u hospitales',
        },
        { value: 14, option: 'Otros' },
      ];
      return this.resolveOptions(options);
    },
    resolveOptions(options) {
      let promise = new Promise(async (resolve) => {
        let optionsResponse = [];
        options.forEach((element) => {
          optionsResponse.push({
            Value: element.value ? element.value : element.option,
            Display: element.option,
            isSelected: false,
          });
        });
        resolve(optionsResponse);
      });
      return promise;
    },
    async cancelClicked() {
      let message = '¿Está seguro que desea cancelar la creación del PEI o PS?';
      if (this.isFromTab1) {
        message =
          '¿Está seguro que desea cancelar la actualización del PEI o PS?';
      }
      const answer = await mixAlert.methods.ShowCancelModal(
        'Atención',
        message,
        'Sí',
        'No'
      );
      if (answer) {
        if (this.isFromTab1) router.push({ path: '/pei/detail' });
        else router.push({ path: '/pei/history' });
      }
    },
    async btnContinueClick() {
      let currentQuestionGroup = this.$refs[
        'questionGroup_' + this.currentStepIndex
      ][0];
      let isValid = currentQuestionGroup.validate();
      if (
        this.currentStep.stepNumber == 2 &&
        this.showElegibilityDeterminationConditions
      ) {
        if (
          this.peiEligibilityDeterminationData
            .eligibilityDeterminationConditions.selectedItems.length == 0
        )
          return this.ShowToast(
            '',
            'Debe seleccionar al menos una condición para continuar',
            'error'
          );
      }
      if (this.currentStep.stepNumber == 3) {
        currentQuestionGroup.isValid = isValid && !this.isInvalidStep3;
        await this.loadQuestion18();
        await this.applyExtendedSchoolYearStartDateRule();
      }
      if (currentQuestionGroup.isValid) {
        await currentQuestionGroup.continue();
      }
    },
    async continueClicked(questionGroup) {
      if (questionGroup.isValid) {
        const question2dot0 = this.getQuestionElement(1, 2.05);
        const question2dot1 = this.getQuestionElement(1, 2.1);
        let nextStepIndex = 0;

        // si es en el paso de inicio y tiene seleccionado hacer copia de PEI
        if (this.currentStepIndex == 1) {
          if (question2dot1.selectedAnswer) {
            this.toWizardLastStep(true);
          } else if (question2dot0.selectedAnswer) {
            this.toWizardLastStep();
          }
        }
        //si hacemos click en el paso de creación
        if (this.currentStepIndex == this.steps.length) {
          //se crea copia de pei
          if (question2dot1.selectedAnswer) {
            if (this.PeiGeneral.ConfigUnilateralFlag == '1') {
              await this.createPSCopy();
              return;
            }
            await this.createPeiCopy();
            return;
          } else if (question2dot0.selectedAnswer) {
            await this.createPeiCopy();
            return;
          }

          this.mapQuestionsAnswers();

          // JSF: sí esta en modo lectura, no hay necesidad de guardar ni crear pei nuevo
          if (!this.isReadOnly) {
            // si tenemos peiId desde el router es porque venimos de la pantalla detalle PEI y vamos a actualizar
            // en caso contrario es un Pei nuevo
            if (this.peiId) {
              this.PeiGeneral.PeiId = this.peiId;
              await this.updatePei();
            } else {
              await this.createPei();
            }
          } else {
            this.setPeiId(this.peiId);
            this.$router.push({ path: '/pei/detail' });
          }
        } else if (
          this.currentStepIndex < this.steps.length &&
          this.currentStep.isValid
        ) {
          if (this.nextStep.validatePreviousYear) {
            this.nextStep.showStep = await this.hasSignedPei(
              this.studentSieId,
              this.RealAcademicYear - 1
            );
          }

          let peiId = this.studentInfo.mipeStudent.peiId;
          let result = await this.getRecommendedLocation(peiId);
          this.recommendedLocation = result && result.description;

          if (this.nextStep.showStep || this.nextStep.stepNumber == 4) {
            // JSF: Acorde a la US95455 (User Story 95455: Define PEI: Pregunta 12 (Medición))
            // se debe poner modo lectura la pregunta #12 del paso 4.
            const question12 = this.getQuestionElement(4, 11);
            if (question12) {
              question12.isDisabled = true;
            }

            if (
              this.nextStep.stepNumber == 2 &&
              !this.getQuestionElement(2, 2).isVisible &&
              !this.isFromPeiDetail
            ) {
              await this.hasAgencyLastYearSignedPei();
            }

            let question1Step2Element = this.getQuestionElement(2, 1);

            //Show Order on Additional Question in DisplayNumber
            let lastNumber = 0;
            this.orderQuestion = 1;
            this.steps[1].questions.forEach((i) => {
              if (i.isVisible) {
                if (Math.trunc(i.number) == lastNumber) {
                  i.displayNumber = `P${this.orderQuestion}`;
                } else {
                  this.orderQuestion++;
                  i.displayNumber = `P${this.orderQuestion}`;
                }
              }
              lastNumber = i.number;
            });

            question1Step2Element.text =
              '<p>Según se seleccionó en el PEI o PS del año escolar <b>' +
              (this.RealAcademicYear - 2) +
              '-' +
              (this.RealAcademicYear - 1) +
              '</b> la alternativa de ubicación recomendada fue <b>' +
              this.recommendedLocation +
              '</b>, ' +
              ' ¿se tiene evidencia de que el estudiante participó de esta alternativa de ubicación? </p>';

            nextStepIndex = this.currentStepIndex + 1;
          } else {
            //Jump one step
            nextStepIndex = this.currentStepIndex + 2;
          }
          this.setCurrentStep(nextStepIndex);
        }
      }
    },
    setCurrentStep(stepIndex) {
      this.currentStep.active = false;
      this.currentStep.completed = true;
      this.currentStepIndex = stepIndex;
      this.currentStep.active = true;
    },
    backClicked() {
      // si es en el paso de inicio y tiene seleccionado hacer copia de PEI
      if (this.currentStepIndex == 5) {
        let question2dot1 = this.getQuestionElement(1, 2.1);
        let question2dot05 = this.getQuestionElement(1, 2.05);

        if (question2dot1.selectedAnswer || question2dot05.selectedAnswer) {
          this.toWizardFirstStep();
          this.onQuestion2dot0ValueChange(question2dot05);
          return;
        }
      }

      let step4 = this.getStep(4);

      if (this.currentStep.stepNumber == this.steps.length) {
        this.currentStep.active = false;
        this.currentStep.completed = false;
        this.currentStepIndex = this.steps.length - 2;
        this.currentStep.active = true;
        step4.showStep = false;
      } else {
        if (this.steps[this.currentStepIndex - 2].showStep) {
          this.currentStep.active = false;
          this.currentStep.completed = false;
          this.currentStepIndex--;
          this.currentStep.active = true;
        } else {
          this.currentStep.completed = false;
          this.currentStep.active = false;
          this.currentStepIndex = this.currentStepIndex - 2;
          this.currentStep.active = true;
        }
        step4.showStep = true;
      }
    },
    async getPeiType(studentSieId, academicYearId) {
      return await new Promise((resolve) => {
        this.$ApiGet(
          '/api/pei/getpeitype?studentSieId=' +
            studentSieId +
            '&academicYearId=' +
            academicYearId
        )
          .then((res) => {
            if (res && res.data != null) {
              if (res.data == 4) {
                resolve('El primero');
              } else {
                resolve('Revisión');
              }
            }
          })
          .catch((err) => {
            this.response = err;
          });
      });
    },
    async getPeiYear() {
      return await this.$ApiGet(
        '/api/pei/getpeiYear?peiId=' + this.SelectedPeiId
      )
        .then((res) => {
          this.SelectedPeiYear = res.data.year;
          this.SelectedPeiYearId = res.data.id;
          this.SelectedPeiYearText = res.data.description;
        })
        .catch((err) => {
          this.response = err;
        });
    },
    async hasSignedPei(studentSieId, year) {
      return await new Promise((resolve) => {
        this.$ApiGet(
          '/api/pei/hassignedpei?studentSieId=' + studentSieId + '&year=' + year
        )
          .then((res) => {
            let result = res.data != null && res.data;
            resolve(result);
          })
          .catch((err) => {
            this.response = err;
          });
      });
    },
    async createPeiCopy() {
      let question1Selection = this.getQuestionElement(1, 1);
      let self = this;
      const question2dot0 = this.getQuestionElement(1, 2.05);
      const payload = {
        PeiId: `${self.signedPeiId}`,
        ConfigAcademicYearId: question1Selection.selectedAnswerId,
        StudentSieId: Number(self.studentSieId),
      };
      if (question2dot0.selectedAnswer) {
        payload.ConfigUnilateralFlag = 2;
      }
      return await new Promise((resolve) => {
        self
          .$ApiPost('/api/pei/createpeicopy', payload)
          .then((res) => {
            if (res && res.data != null) {
              resolve(res.data);

              //redirigir a detalle de PEI

              self.setPeiId(res.data.peiId);
              self.setLocationRejected(res.data.configLocationRejectFlag);
              self.SynchronizeAnalisisServices(res.data.peiId);
              const path =
                payload.ConfigUnilateralFlag == 2
                  ? '/pei/history'
                  : '/pei/amendment';
              self.$router.push({ path });
            }
          })
          .catch(function(err) {
            self.showErrorMessage(err);
          });
      });
    },
    async createPSCopy() {
      let question1Selection = this.getQuestionElement(1, 1);
      let self = this;
      return await new Promise((resolve) => {
        self
          .$ApiPost('/api/pei/createpscopy', {
            PeiId: `${self.signedPeiId}`,
            ConfigAcademicYearId: question1Selection.selectedAnswerId,
            StudentSieId: Number(self.studentSieId),
          })
          .then((res) => {
            if (res && res.data != null) {
              resolve(res.data);

              //redirigir a detalle de PEI
              self.setPeiId(res.data.peiId);
              self.setLocationRejected(res.data.configLocationRejectFlag);
              self.SynchronizeAnalisisServices(res.data.peiId);
              self.$router.push({ path: '/pei/amendment' });
            }
          })
          .catch(function(err) {
            self.showErrorMessage(err);
          });
      });
    },
    async getRecommendedLocation(peiId) {
      return await new Promise((resolve) => {
        this.$ApiGet('/api/pei/getrecommendedlocation?peiId=' + peiId)
          .then((res) => {
            if (res) {
              resolve(res.data);
            }
          })
          .catch((err) => {
            this.response = err;
          });
      });
    },
    async getAlternateLocations() {
      return await new Promise((resolve) => {
        this.$ApiGet('/api/pei/getalternatelocations')
          .then((res) => {
            if (res) {
              resolve(res.data);
            }
          })
          .catch((err) => {
            this.response = err;
          });
      });
    },
    async updateStudentStatus(studentSie, status) {
      return await new Promise((resolve) => {
        this.$ApiPut(
          '/api/student/changestatus?studentsie=' +
            studentSie +
            '&status=' +
            status
        )
          .then((res) => {
            if (res) {
              resolve(res.data);
            }
          })
          .catch((err) => {
            this.showMessage(err, messageTypes.Error);
          });
      });
    },
    getStep(number) {
      return this.steps.find((step) => step.stepNumber == number);
    },
    getStepElement(number) {
      let stepElement = this.$refs['questionGroup_' + number];
      if (stepElement != null) {
        return stepElement[0];
      }
      return null;
    },
    async isAgeValidForAcademicYear() {
      let studentId = this.$store.getters.SelectedStudent.studentId;
      return await new Promise((resolve) => {
        this.$ApiGet(
          '/api/student/isAgeValidForAcademicYear?studentSie=' +
            studentId +
            '&year=' +
            this.RealAcademicYear
        )
          .then((res) => {
            if (res) {
              resolve(res.data);
            }
          })
          .catch((err) => {
            this.response = err;
          });
      });
    },
    setQuestionVisible(stepNumber, questionNumber, isVisible) {
      let question = this.getQuestionElement(stepNumber, questionNumber);
      if (question) {
        question?.clear();
        question.isVisible = isVisible;
      }
    },
    getQuestionElement(stepNumber, questionNumber) {
      let stepElement = this.getStepElement(stepNumber);
      if (stepElement != null) {
        return stepElement.getQuestion(questionNumber);
      }
      return null;
    },
    getQuestionField(stepNumber, questionNumber, fieldId) {
      let questionElement = this.getQuestionElement(stepNumber, questionNumber);
      if (questionElement != null) {
        return questionElement.getField(fieldId);
      }
      return null;
    },
    async hasImpedimentYearBefore() {
      let self = this;
      await this.GetLatestSignedPEI(this.RealAcademicYear - 1).then(
        (result) => {
          if (result.data != null) {
            let question6 = this.getQuestionElement(2, 6);

            let yearFrom = self.RealAcademicYear - 2,
              yearTo = self.RealAcademicYear - 1;
            question6.text = question6.text.replace(
              '(anterior)',
              '<b>' + yearFrom + '-' + yearTo + '</b>'
            );
            if (result.data.configMobilityProblemFlag == 1) {
              this.setQuestionVisible(2, 6, true);

              let question6A = this.getQuestionElement(2, 6.1);

              this.steps[1].questions.forEach((i) => {
                if (i.number == 6) {
                  i.isVisible = true;
                }
              });

              question6A.text =
                '<p>¿Estas necesidades fueron expresadas en el PEI o PS del año escolar <b>' +
                yearFrom +
                '-' +
                yearTo +
                '</b>?</p> ';
            }
          }
        }
      );
    },
    async onQuestion1ValueChange(question) {
      this.setQuestionVisible(1, 2.05, false);
      if (question.selectedAnswer) {
        this.academicYear = question.selectedAnswerText
          ? question.selectedAnswerText
          : this.SelectedPeiYearText;

        this.RealAcademicYear = question.selectedAnswer;
        let yearId = question.selectedAnswerId
          ? question.selectedAnswerId
          : this.SelectedPeiYearId;

        let canDefinePei = await this.candefinepeiacademicyear(
          this.studentSieId,
          yearId
        );
        if (!canDefinePei && !this.isFromPeiDetail) {
          question.invalidSelection = true;
          question.validationResult.messageText = this.getValidationMessage(
            '5543701'
          );
          return;
        } else {
          question.invalidSelection = false;
          question.validationResult.messageText = '';
        }

        this.revisionType = await this.getPeiType(this.studentSieId, yearId);

        this.hasPeioPSTothisAcademciYearSelected = await this.hasSignedPei(
          this.studentSieId,
          question.selectedAnswer
        );

        this.hasSignedPeiLastYear = await this.hasSignedPei(
          this.studentSieId,
          this.RealAcademicYear - 1
        );
        let self = this;
        this.steps.forEach((step) => {
          if (step.stepNumber == 4) {
            step.questions.forEach((question) => {
              if (question.number == 6) {
                question.getOptions = self.getGrados;
                question.isVisible = true;
              }
            });
          }
        });
        this.resetWizard();
        this.setQuestionVisible(1, 2, true);
        let question1Element = this.getQuestionElement(1, 2);

        question1Element.text =
          '<p>¿Este PEI o PS se considera <b>' +
          this.revisionType +
          '</b> para el año <b>' +
          this.academicYear +
          '</b>?</p>';

        let pei = await this.getPeiPsNumberByYearSeiId(this.RealAcademicYear);
        this.latestSignedPei = pei;
        if (pei != null && pei != '') {
          let question2dot1Element = this.getQuestionElement(1, 2.1);
          this.signedPeiId = parseInt(pei.peiId);
          question2dot1Element.text =
            '<p>¿Desea crear una copia de los datos ingresados para el PEI o PS  <b>' +
            this.formatPeiNumber(pei.peiId) +
            '<b/>?</p> ';
        }

        let question2Element = this.getQuestionElement(1, 2);
        let question3Element = this.getQuestionElement(1, 3);
        question2Element.clear();
        if (question3Element != null) {
          question3Element.clear();
        }
        this.setQuestionVisible(1, 2.1, false);
        this.hasImpedimentYearBefore();

        let questionRegions = this.getQuestionElement(4, 5.1);
        questionRegions.refreshOptions();

        let studentAge = await this.getStudentAgeByAcademicYear();

        this.studentConfAgeGrade = await this.getStudentConfigGradeByAcademicYear();
        this.getGrados();

        let isAgeValid = studentAge < peiTransitionAges.Six;
        let question4Element = this.getQuestionElement(3, 8);
        question4Element.selectedAnswer = isAgeValid;

        let hasAlmostSix =
          studentAge >= peiTransitionAges.Six && studentAge < 7;

        question4Element.disabled = !hasAlmostSix;
      }
    },
    async onQuestion2ValueChange(question) {
      question.invalidSelection = !question.selectedAnswer;
      this.setQuestionVisible(1, 2.05, false);
      if (question.invalidSelection) {
        question.validationResult.messageText = this.getValidationMessage('4');
        return;
      }
      if (this.canCreatePSC) {
        this.setQuestionVisible(1, 2.05, true);
        const question2dot0 = this.getQuestionElement(1, 2.05);
        await this.onQuestion2dot0ValueChange(question2dot0);
      } else {
        const question2dot1 = this.getQuestionElement(1, 2.1);
        question2dot1.displayNumber = '3';
        question2dot1.subNumber = '';
        this.validationForQuestion2a(question.selectedAnswer);
      }
    },
    validationForQuestion2a(show) {
      const show2dot1 =
        show &&
        this.RealAcademicYear != 0 &&
        this.hasPeioPSTothisAcademciYearSelected;
      this.setQuestionVisible(1, 2.1, show2dot1);
    },
    async onQuestion2dot0ValueChange(question) {
      question.displayNumber = '3';
      question.subNumber = '';
      question.validationResult.resultType = validationResultTypes.Good;
      question.validationResult.messageText = '';

      question.invalidSelection = this.isPscRequired || this.isPscOptional;
      if (!question.invalidSelection) return;

      question.validationResult.resultType = validationResultTypes.Warning;
      question.validationResult.messageText = `Este estudiante ha cumplido la mayoría de edad (${AGE_LIMIT} años). Por lo que se require la creación de un Plan de Servicios Compensatorios.`;

      const isInvalid =
        this.isPscRequired &&
        question.selectedAnswer === false &&
        !this.hasPscCreationPermission;
      if (isInvalid) {
        question.validationResult.resultType = validationResultTypes.Error;
        question.validationResult.messageText = `Este estudiante cumplirá la mayoría de edad (${AGE_LIMIT} años) dentro del periodo seleccionado y debe crear el plan de servicios compensatorios para continuar.`;
      } else if (
        this.isPscRequired &&
        question.selectedAnswer === false &&
        this.hasPscCreationPermission &&
        !this.hasLocationSchoolStudent
      ) {
        question.validationResult.resultType = validationResultTypes.Error;
        question.validationResult.messageText = `La escuela del estudiante no es valida. (22042).`;
      } else if (this.isPscOptional) {
        question.invalidSelection = question.selectedAnswer !== false;
        question.validationResult.resultType = validationResultTypes.Warning;
        question.validationResult.messageText = question.validationResult.messageText
          .replace('ha cumplido', 'cumplirá')
          .replace('require', 'recomienda')
          .replace(')', ') dentro del periodo seleccionado');
        if (!(this.studentAgeAtAcademicYearEnd >= AGE_LIMIT)) {
          question.invalidSelection = false;
          question.validationResult.resultType = validationResultTypes.Good;
          question.validationResult.messageText = '';
        }
        const question2dot1 = this.getQuestionElement(1, 2.1);
        question2dot1.displayNumber = '4';
        question2dot1.subNumber = '';

        this.validationForQuestion2a(question.selectedAnswer == false);
      }

      if (question.selectedAnswer) {
        this.loading = true;
        const res = await peiService.getEducationalCompensatoryServiceBalance(
          this.studentSieId,
          true
        );
        this.loading = false;

        const hasEducationalCompensatoryServiceDue = res?.data > 0;

        let hasTherapyCompensatoryServiceDue = false;

        // only get therapy compensatory service due if there is no educational compensatory service due
        if (
          !hasEducationalCompensatoryServiceDue ||
          // or the student does not have school and has permission to create PSC without school
          (!this.hasLocationSchoolStudent && this.canCreatePscWithoutSchool)
        ) {
          // check if the student has therapy compensatory service due
          const {
            data: hasInterventionsDuePending,
          } = await hasInterventionsDue(this.studentSieId);

          hasTherapyCompensatoryServiceDue =
            hasInterventionsDuePending === true;
        }

        // If the student has school and has educational or therapy compensatory service due,
        // the PSC can be created
        if (
          this.hasLocationSchoolStudent &&
          (hasEducationalCompensatoryServiceDue ||
            hasTherapyCompensatoryServiceDue)
        ) {
          return;
        }

        question.invalidSelection = res?.data <= 0;
        if (question.invalidSelection) {
          if (this.canCreatePscWithoutSchool) {
            // Permanece lógica del permiso para los estudiantes que no tienen escuela
            if (
              hasTherapyCompensatoryServiceDue &&
              !this.hasLocationSchoolStudent
            ) {
              return;
            }
          }

          question.validationResult.resultType = validationResultTypes.Error;
          question.validationResult.messageText =
            'Se identificó que el estudiante no cuenta con Servicios Compensatorios adeudados, por lo que NO se podrá crear el PSC.';
        }
      }
    },
    async hasAgencyLastYearSignedPei() {
      await this.GetLatestSignedPEI(this.RealAcademicYear - 1).then(
        (result) => {
          if (result.data) {
            //pregunta 2
            this.getQuestionElement(2, 2).text =
              '<p> En la Parte V del PEI <b>' +
              (this.RealAcademicYear - 2) +
              '-' +
              (this.RealAcademicYear - 1) +
              '</b> el estudiante tiene un programa de servicios' +
              ' con actividades dirigidas al proceso de transición postsecundario </p>';

            this.setQuestionVisible(
              2,
              2,
              result.data.configTransitionServicesFlag == '1'
            );
            this.steps[1].questions.forEach((i) => {
              if (i.number == 2) {
                i.isVisible = result.data.configTransitionServicesFlag == '1';
              }
            });
            //pregunta 3
            this.getQuestionElement(2, 3).text =
              'En la Parte IX del PEI <b>' +
              (this.RealAcademicYear - 2) +
              '-' +
              (this.RealAcademicYear - 1) +
              '</b> se estableció la necesidad de invitar a otras agencias para proveer algún servicio, ¿existe evidencia de que la agencia proveyó el servicio?';
            this.setQuestionVisible(2, 3, result.data.hasParticipantsAgencies);
            this.steps[1].questions.forEach((i) => {
              if (i.number == 3) {
                i.isVisible = result.data.hasParticipantsAgencies;
              }
            });
          }
        }
      );
    },
    isElegible() {
      let student = this.studentInfo;

      if (student.mipeStudent != null) {
        let studentStatus = student.mipeStudent.studentStatusCode;

        if (studentStatus == StudentStatuses.Elegible) return true;
        return false;
      }

      return false;
    },
    onQuestion2Step1ValueChange(question) {
      if (question.selectedAnswer != undefined && !question.selectedAnswer) {
        this.setQuestionVisible(2, 1.1, true);
        this.setQuestionVisible(2, 1.2, true);
      } else {
        this.setQuestionVisible(2, 1.1, false);
        this.setQuestionVisible(2, 1.2, false);
      }
    },
    onQuestion2Step2ValueChange(question) {
      if (question.selectedAnswer != undefined && question.selectedAnswer) {
        this.setQuestionVisible(2, 2.1, false);
      } else {
        this.setQuestionVisible(2, 2.1, true);
      }
    },
    onQuestion3ValueChange(question) {
      if (question.selectedAnswer != undefined && question.selectedAnswer) {
        this.setQuestionVisible(2, 3.1, false);
      } else {
        this.setQuestionVisible(2, 3.1, true);
      }
    },
    onQuestion3AValueChange() {},
    onQuestion4ValueChange(question) {
      let displayNumber = question.displayNumber;
      if (question.selectedAnswer != undefined && question.selectedAnswer) {
        this.setQuestionVisible(2, 4.1, true);
        this.steps[1].questions.forEach((i) => {
          if (i.number == 4.1) {
            i.displayNumber = displayNumber;
          }
        });

        this.setQuestionVisible(2, 4.2, true);
        this.steps[1].questions.forEach((i) => {
          if (i.number == 4.2) {
            i.displayNumber = displayNumber;
          }
        });

        this.steps[1].questions.forEach((i) => {
          if (i.number == 4.3) {
            i.displayNumber = displayNumber;
          }
        });
      } else {
        let question4aElement = this.getQuestionElement(2, 4.1);
        let question4bElement = this.getQuestionElement(2, 4.2);
        let question4cElement = this.getQuestionElement(2, 4.3);

        question4aElement.clear();
        question4bElement.clear();
        question4cElement.clear();

        this.setQuestionVisible(2, 4.1, false);
        this.setQuestionVisible(2, 4.2, false);
        this.setQuestionVisible(2, 4.3, false);
      }
    },
    onQuestion4AValueChange(question) {
      if (question.selectedAnswer != undefined && question.selectedAnswer) {
        this.setQuestionVisible(2, 4.3, true);
      } else {
        let question4cElement = this.getQuestionElement(2, 4.3);
        question4cElement.clear();
        this.setQuestionVisible(2, 4.3, false);
      }
    },
    formatDateForPicker(date) {
      if (date != null) {
        date = date
          .toLocaleDateString('es-ES', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })
          .replace(/ /g, '/');

        let firstSlash = date.indexOf('/');
        let formatted =
          date.substring(0, firstSlash) +
          '/' +
          date.charAt(firstSlash + 1).toUpperCase() +
          date.slice(firstSlash + 2);

        return formatted;
      }
      return null;
    },
    //evento provisional para evaluar cambio de status
    async CambioStatus() {
      let studentId = this.$store.getters.SelectedStudent.studentId;

      await this.updateStudentStatus(studentId, StudentStatuses.Servido);
    },
    onQuestion6ValueChange(question) {
      if (question.selectedAnswer != undefined && question.selectedAnswer) {
        this.setQuestionVisible(2, 6.1, true);
        this.setQuestionVisible(2, 6.2, true);
        this.setQuestionVisible(2, 6.7, true);
        let quest = this.steps[1].questions.find((i) => i.number == 6);
        this.steps[1].questions.forEach((i) => {
          if (Math.trunc(i.number) == 6) i.displayNumber = quest.displayNumber;
        });
      } else {
        this.setQuestionVisible(2, 6.1, false);
        this.setQuestionVisible(2, 6.2, false);
        this.setQuestionVisible(2, 6.3, false);
        this.setQuestionVisible(2, 6.4, false);
        this.setQuestionVisible(2, 6.5, false);
        this.setQuestionVisible(2, 6.6, false);
        this.setQuestionVisible(2, 6.7, false);
      }
    },
    onQuestion6AValueChange(question) {
      let fieldComment = this.getQuestionElement(2, 6.1).getField('comentario');
      if (question.selectedAnswer != undefined && question.selectedAnswer) {
        fieldComment.visible = false;
        this.setQuestionVisible(2, 6.7, true);
        this.setQuestionVisible(2, 6.2, true);
      } else {
        fieldComment.visible = true;
        this.setQuestionVisible(2, 6.7, false);
        this.setQuestionVisible(2, 6.2, false);
      }
    },
    onQuestion6BValueChange(question) {
      let fieldComment = this.getQuestionElement(2, 6.2).getField('comentario');
      if (question.selectedAnswer != undefined && question.selectedAnswer) {
        fieldComment.visible = false;
        this.setQuestionVisible(2, 6.3, true);
      } else {
        fieldComment.visible = true;
        this.setQuestionVisible(2, 6.3, false);
      }
    },
    onQuestion6CValueChange(question) {
      let fieldComment = this.getQuestionElement(2, 6.3).getField('comentario');
      if (question.selectedAnswer != undefined && question.selectedAnswer) {
        this.setQuestionVisible(2, 6.4, true);
        this.setQuestionVisible(2, 6.5, true);
        this.setQuestionVisible(2, 6.6, true);
        fieldComment.visible = false;
      } else {
        this.setQuestionVisible(2, 6.4, false);
        this.setQuestionVisible(2, 6.5, false);
        this.setQuestionVisible(2, 6.6, false);
        fieldComment.visible = true;
      }
    },
    onQuestion6dot4FieldAjustesChange(event, field) {
      let fieldComment = this.getQuestionElement(2, 6.4).getField('comentario');
      fieldComment.visible = field.selectedOptions.includes(14);
    },
    onQuestion6FValueChange(question) {
      let fieldComment = this.getQuestionElement(2, 6.6).getField('comentario');
      if (question.selectedAnswer != undefined && question.selectedAnswer) {
        fieldComment.visible = false;
      } else {
        fieldComment.visible = true;
      }
    },
    onQuestion6GValueChange(question) {
      let fieldComment = this.getQuestionElement(2, 6.7).getField('comentario');
      fieldComment.visible = !(
        question.selectedAnswer != undefined && question.selectedAnswer
      );
    },
    onQuestion7ValueChange(question) {
      if (question.selectedAnswer != undefined && !question.selectedAnswer) {
        question.invalidSelection = true;
        question.validationResult.messageText =
          'Pregunta 3: Usted no está de acuerdo con el impedimento \
                                 y no se puede proseguir. Favor de corregir el impedimento en MiPE para poder continuar.';
      } else {
        question.invalidSelection = false;
      }
    },
    async onQuestion8ValueChange(question) {
      if (question.selectedAnswer) {
        let isAgeValid = await this.isAgeValidForAcademicYear();
        if (!isAgeValid) {
          question.invalidSelection = true;
          question.validationResult.messageText =
            'Los estudiantes ubicados en un Head Start, \
                                                            Hogar o Centro de Cuido deben tener edad \
                                                            preescolar (2 años y 6 meses - 5 años). (41)';
        }
      } else {
        question.invalidSelection = false;
      }
    },
    async onQuestion9ValueChange(question) {
      if (question.selectedAnswer) {
        this.setQuestionVisible(3, 9.1, false);
        this.setQuestionVisible(3, 9.2, false);

        let question1Step1Element = this.getQuestionElement(1, 1);
        let studentEnrollment = await this.getStudentEnrollment(
          this.studentSieId,
          question1Step1Element.selectedAnswerId
        );

        //si el estudiante no cuenta con matrí­cula activa
        if (studentEnrollment == null) {
          question.invalidSelection = true;
          question.validationResult.resultType = validationResultTypes.Warning;
          question.validationResult.messageText =
            'Pregunta 5: Su contestación no va de acuerdo con el SIE. \
                                                            Si este es un error, favor corregirlo en el SIE. ¿Está seguro(a) \
                                                            que desea continuar?. (10)';
        } else {
          question.invalidSelection = false;
        }
      } else {
        question.invalidSelection = false;
        this.setQuestionVisible(3, 9.1, true);
      }
      this.updateSchools();
    },
    onQuestion9dot1ValueChange(question) {
      let question8Answer = this.getQuestionElement(3, 8);
      if (
        question.selectedAnswer &&
        question8Answer != undefined &&
        !question8Answer.selectedAnswer
      ) {
        question.invalidSelection = true;
        question.validationResult.messageText =
          'Si el estudiante estará ubicado en un Head Start o \
           Centro de Cuido, debe contestar Sí a la pregunta ¿El estudiante tiene la edad pertinente para cursar un grado pre-escolar?';

        this.setQuestionVisible(3, 9.2, false);
      } else {
        if (question.selectedAnswer) {
          this.setQuestionVisible(3, 9.2, false);
        } else {
          this.setQuestionVisible(3, 9.2, true);
        }
        question.invalidSelection = false;
      }
      this.updateSchools();

      if (!question.selectedAnswer && this.oldEPKValue) {
        this.oldEPKValue = false;
        let questionGrades = this.getQuestionElement(4, 6);
        questionGrades.refreshOptions();
        questionGrades.disabled = false;
      }
    },
    async onQuestion9dot2ValueChange(question) {
      if (question.selectedAnswer) {
        let isInInstitution = await this.isStudentInInstitution(
          this.studentSieId
        );
        if (!isInInstitution) {
          question.validationResult.messageText =
            'El estudiante debe estar en una Institución.';
          question.invalidSelection = true;
        }
      } else {
        question.invalidSelection = false;
      }
      this.updateSchools();
    },
    async getPeiPsNumberByYearSeiId(academyYear) {
      let response = await this.GetLatestSignedPEI(academyYear);
      return response.data;
    },
    GetLatestSignedPEI(academyYear) {
      return new Promise((resolve, reject) => {
        this.$ApiGet(
          '/api/pei/getlatestsignedpeips?studentSieId=' +
            this.studentSieId +
            '&academicYear=' +
            academyYear
        ).then(
          (response) => resolve(response),
          (error) => reject(error)
        );
      });
    },
    ClearQuestionSelectedAnswers(questions) {
      if (questions) {
        for (let i = 0; i < questions.length; i++) {
          questions[i].selectedAnswer = undefined;
          questions[i].questionOpenAnswer = '';
        }
      }
    },
    getRegions() {
      let promise = new Promise(async (resolve) => {
        let academicyear = !this.isFromPeiDetail
          ? this.getQuestionElement(1, 1).selectedAnswer
          : this.SelectedPeiYear
          ? this.SelectedPeiYear
          : this.getCurrentYear();
        if (academicyear != undefined) {
          let res = await this.$ApiGet(
            '/api/pei/getregions?academicYear=' + academicyear
          );
          let datum = [];
          res.data.forEach((element) => {
            datum.push({
              Value: element.id,
              Display: element.name,
              isSelected: false,
            });
          });
          resolve(datum);
        }
      });
      return promise;
    },
    updateSchools() {
      let nextQuestion = this.getQuestionElement(4, 5.1);
      let isSchool = this.getQuestionElement(3, 9).selectedAnswer;
      let isLocation = this.getQuestionElement(3, 9.1).selectedAnswer;
      let fieldSchoold = nextQuestion.getField('escuela');

      if (isSchool != undefined && isSchool) {
        fieldSchoold.label = '<b>Escuela:</b>';
      } else if (isLocation != undefined && isLocation) {
        fieldSchoold.label = '<b>Tipo de Ubicación:</b>';
      } else {
        fieldSchoold.label = '<b>Instituciones:</b>';
      }

      //nextQuestion.refreshOptions();

      let fieldRegion = nextQuestion.getField('region');
      fieldRegion.value = null;

      let fieldDistrict = nextQuestion.getField('distrito');
      fieldDistrict.value = null;
      fieldDistrict.options = [];

      fieldSchoold.value = null;
      fieldSchoold.options = [];
    },
    getDistricts(academicYear, regionId) {
      let promise = new Promise(async (resolve) => {
        if (academicYear != undefined && regionId != null) {
          let res = await this.$ApiGet(
            '/api/pei/getdistricts?regionId=' +
              regionId +
              '&academicYear=' +
              academicYear
          );
          let datum = [];
          res.data.forEach((element) => {
            datum.push({
              Value: element.id,
              Display: element.name,
              isSelected: false,
            });
          });
          resolve(datum);
        }
      });
      return promise;
    },
    async getGrados() {
      const selectedGradeId = this.PeiGeneralDB?.gradeId;
      const academicYearId = this.getQuestionElement(1, 1).selectedAnswerId;
      const parameters = academicYearId
        ? '?sie=' + this.studentSieId + '&academicYearId=' + academicYearId
        : '?sie=' + this.studentSieId;
      const answerSchool = this.getQuestionField(4, 5.1, 'escuela').value;
      const question4Element = this.getQuestionElement(4, 6);

      const epkGrade = {
        Value: 33,
        Display: 'EPK',
        isSelected: true,
      };
      // if school selected is EPK and user has not permission to edit grade, show EPK grade and disable the question
      if (answerSchool?.isEPK && !this.hasEditGradePermission) {
        question4Element.selectedAnswer = 33;
        question4Element.isDisabled = true;
        this.onQuestion6Step4ValueChange(question4Element);

        // return a list with the EPK grade as the only option
        return [epkGrade];
      }

      const res = await this.$ApiGet('/api/pei/grades/student' + parameters);
      const grades = res.data.map((element) => ({
        Value: element.id,
        Display: element.name,
        isSelected: selectedGradeId
          ? String(element.id) === String(selectedGradeId)
          : false,
      }));

      // Search gradeId from ConfGrade based on student age for selected academicYear.
      const confGrade = this.studentConfAgeGrade;
      const gradeId = grades.find((grade) => grade.Display === confGrade)
        ?.Value;
      const gradeToSelect = selectedGradeId || gradeId;
      if (gradeToSelect) {
        const configImpedimentCognativeFlag = this.getQuestionElement(4, 9)
          .selectedAnswer;
        const isQuestionReadOnly = this.isReadOnlyGradeQuestion_4_6(
          configImpedimentCognativeFlag
        );

        question4Element.selectedAnswer = gradeToSelect;

        if (
          isQuestionReadOnly &&
          !this.canShowSectionEditable &&
          !this.canShowSectionEditableBySubcsectionASectionVI
        ) {
          question4Element.isDisabled = true;
        }

        this.onQuestion6Step4ValueChange(question4Element);
      }

      if (answerSchool?.isEPK) {
        // add the EPK grade to the list of grades at the penultimate position
        const penultimateIndex = Math.max(grades.length - 1, 0);
        grades.splice(penultimateIndex, 0, epkGrade);
      }

      if (grades.length == 1) {
        question4Element.selectedAnswer = grades[0].Value;
        question4Element.isDisabled = true;
      }

      return grades;
    },
    isReadOnlyGradeQuestion_4_6(selectedAnswer) {
      let isReadOnly = this.isReadOnly;

      if (!isReadOnly && selectedAnswer && !this.hasEditGradePermission) {
        isReadOnly = true;
      }

      return isReadOnly;
    },
    onQuestion9Step4ValueChange(question) {
      let question4Element = this.getQuestionElement(4, 6);
      question4Element.isDisabled = this.isReadOnlyGradeQuestion_4_6(
        question.selectedAnswer
      );

      if (question.selectedAnswer) {
        let confGrade = this.studentConfAgeGrade;
        var originalGrade = question4Element.options.find(
          (x) => x.Display == confGrade
        );
        if (question4Element.options.length == 1 && !originalGrade?.Value) {
          question4Element.selectedAnswer = question4Element.options[0].Value;
        } else {
          let selectedGradeId = this.PeiGeneralDB?.gradeId;
          question4Element.selectedAnswer =
            originalGrade?.Value || selectedGradeId;
        }
      } else if (question4Element.options.length == 1) {
        question4Element.selectedAnswer = question4Element.options[0].Value;
        question4Element.isDisabled = true;
      }

      this.updateEvaluationProgram();
    },
    onQuestion6Step4ValueChange(question) {
      if (question.selectedAnswer != undefined) {
        let gradoActual = this.studentInfo.mipeStudent.gradeId;
        let res =
          gradoActual + 1 === question.selectedAnswer ||
          gradoActual === question.selectedAnswer;

        if (!res) {
          question.invalidSelection = true;
          question.validationResult.resultType = validationResultTypes.Warning;
          question.validationResult.messageText =
            'El grado seleccionado no es el mismo o el próximo segun los datos en el sistema.';
        } else {
          question.invalidSelection = false;
        }

        if (question.selectedAnswer == 33) {
          if (question.validationResult.messageText) {
            question.validationResult.messageText =
              question.validationResult.messageText +
              "<br/><div class='d-inline'><em class='fa fa-bell-exclamation' style='font-size: 12px;padding-right: 10px;' aria-hidden='true'></em></div>";
          }
          question.validationResult.messageText =
            question.validationResult.messageText +
            'EPK = Estudiante Grado Prescolar.';
        }
      }

      this.updateEvaluationProgram();
    },
    onQuestion7Step4ValueChange(question) {
      this.validateJuvenilInstitution();
    },
    // US 44262 : validate if school selected is type juvenile to apply validations
    validateJuvenilInstitution() {
      const question9 = this.getQuestionElement(4, 7); // Pregunta #9 con el texto: ¿El estudiante muestra conducta inapropiada?
      const answerSchool = this.getQuestionField(4, 5.1, 'escuela').value;

      const hasError =
        answerSchool != null &&
        answerSchool.isJuvenile &&
        question9?.selectedAnswer === false;
      if (hasError) {
        question9.validationResult.resultType = validationResultTypes.Error;
        question9.validationResult.messageText =
          'Al seleccionar una Institución Juvenil será requerido contestar Sí a la pregunta.';
      }
      // show error message
      question9.invalidSelection = hasError;
    },
    getSchools(
      academicYear,
      district,
      isUnilateral,
      isPublic,
      isSpecialSchool,
      isInstitution
    ) {
      let promise = new Promise(async (resolve) => {
        let SchoolTypesEnum = {
          CareCenter: 246,
          PrivateSchool: 248,
          PublicSchool: 249,
          HeadStart: 250,
          Home: 251,
          Institutions: 349,
        };

        let schoolTypes = [];

        if (isSpecialSchool) {
          schoolTypes.push(SchoolTypesEnum.CareCenter);
          schoolTypes.push(SchoolTypesEnum.HeadStart);
          schoolTypes.push(SchoolTypesEnum.Home);
        }
        if (isPublic != undefined && isPublic) {
          schoolTypes.push(SchoolTypesEnum.PublicSchool);
        }
        if (isInstitution) {
          schoolTypes.push(SchoolTypesEnum.Institutions);
        } else if (isUnilateral == '1') {
          schoolTypes.push(SchoolTypesEnum.PrivateSchool);
        }

        if (district != undefined) {
          let academicYearIdRequest =
            academicYear === undefined ? '' : `academicYear=${academicYear}&`;
          let schoolTypesParam =
            schoolTypes.length > 0
              ? `&schoolTypes=${schoolTypes.join('&schoolTypes=')}`
              : '';
          let requestUrl = `/api/schools/?${academicYearIdRequest}districtId=${district}${schoolTypesParam}`;
          let res = await this.$ApiGet(requestUrl);
          let datum = [];
          res.data.forEach((element) => {
            datum.push({
              Value: element.id,
              Display: element.name,
              isEPK: element.isEPK,
              casaFlag: element.casaFlag,
              isJuvenile: element.isJuvenile,
              isSelected: false,
            });
          });
          resolve(datum);
        }
      });
      return promise;
    },
    onQuestion8Step4ValueChange(question) {
      let nextQuestion = this.getQuestionElement(4, 8.1);
      if (question.selectedAnswer != undefined && question.selectedAnswer) {
        let isPreSchool =
          this.studentAge >= peiTransitionAges.TwoAndSevenMonth &&
          this.studentAge <= peiTransitionAges.Six;

        let showNextQuestion =
          isPreSchool || this.studentAge >= peiTransitionAges.Fourteen;

        if (showNextQuestion) {
          nextQuestion.isVisible = true;
          nextQuestion.isRequired = true;
          nextQuestion.refreshOptions();
        } else {
          question.invalidSelection = true;
          question.validationResult.resultType = validationResultTypes.Error;
          question.validationResult.messageText =
            'Este estudiante no tiene edad elegible para trabajar transición.';
        }
      } else {
        question.invalidSelection = false;
        nextQuestion.isVisible = false;
        nextQuestion.isRequired = false;
      }
    },
    onQuestion16Step4ValueChange(question) {
      let nextQuestion = this.getQuestionElement(4, 17);
      let secondNextQuestion = this.getQuestionElement(4, 18);
      if (question.selectedAnswer != undefined && question.selectedAnswer) {
        nextQuestion.isVisible = true;
        nextQuestion.isRequired = true;
        nextQuestion.refreshOptions();
        secondNextQuestion.isVisible = true;
        secondNextQuestion.isRequired = true;
        secondNextQuestion.refreshOptions();
      } else {
        question.invalidSelection = false;
        nextQuestion.isVisible = false;
        nextQuestion.isRequired = false;
        secondNextQuestion.isVisible = false;
        secondNextQuestion.isRequired = false;
      }
    },
    onQuestion15Step4ValueChange(question) {
      let nextQuestion = this.getQuestionElement(4, 19);
      if (question.selectedAnswer != undefined && question.selectedAnswer) {
        nextQuestion.isVisible = true;
        nextQuestion.isRequired = true;
        nextQuestion.refreshOptions();
      } else {
        question.invalidSelection = false;
        nextQuestion.isVisible = false;
        nextQuestion.isRequired = false;
      }
    },
    getTransitions() {
      let promise = new Promise(async (resolve) => {
        let isPreSchool =
          this.studentAge >= peiTransitionAges.TwoAndSevenMonth &&
          this.studentAge <= peiTransitionAges.Six;

        let res = [];

        if (isPreSchool || this.studentAge >= peiTransitionAges.Fourteen) {
          res = await this.$ApiGet(
            '/api/pei/gettransitions?isPreSchool=' + isPreSchool
          );
        }

        let datum = [];
        if (res.data) {
          res.data.forEach((element) => {
            datum.push({
              Value: element.id,
              Display: element.name,
              isSelected: false,
            });
          });
        }
        resolve(datum);
      });
      return promise;
    },
    getMeasurePrograms() {
      let measurePrograms = [
        { value: 16, option: 'Regular' },
        { value: 17, option: 'Evaluación Alterna' },
        { value: 18, option: 'No Aplica' },
      ];
      return this.resolveOptions(measurePrograms);
    },
    onQuestion11Step4ValueChange(question) {
      let nextQuestion = this.getQuestionElement(4, 11.1);
      if (
        question.selectedAnswer != undefined &&
        question.selectedAnswer === 16 &&
        this.PeiGeneralDB.unilateralFlag != '1'
      ) {
        nextQuestion.isVisible = true;
        nextQuestion.isRequired = true;
      } else {
        nextQuestion.isVisible = false;
        nextQuestion.isRequired = false;
      }
    },
    onQuestion12Step4ValueChange(question) {
      question.invalidSelection = true;
      question.validationResult.resultType = validationResultTypes.Warning;
      question.validationResult.messageText =
        'Al seleccionar que no requiere equipo de Asistencia Tecnológica estos serán removidos del PEI y se le notificará al CAAT a través de la garantía Inventario de Equipos AT SAEE-AT-2016-8 que estos serán devueltos.';
    },
    formatPeiNumber(pei) {
      let formatted = pei.substring(0, 4) + '-' + pei.substring(4, pei.length);
      return formatted;
    },
    async getStudentEnrollment(studentSie, academicYearId) {
      let res = await this.$ApiGet(
        '/api/student/getenrollment?studentSie=' +
          studentSie +
          '&academicYearId=' +
          academicYearId
      );
      return res.data;
    },
    async isStudentInInstitution(studentSie) {
      let res = await this.$ApiGet(
        `/api/student/isstudentininstitution?studentSie=${studentSie}`
      );
      return res.data;
    },
    resetWizard() {
      this.isCurrentStepValid = true;
      for (let stepIdx = 0; stepIdx < this.steps.length; stepIdx++) {
        const step = this.steps[stepIdx];
        if (step.questions) {
          for (
            let questionIdx = 0;
            questionIdx < step.questions.length;
            questionIdx++
          ) {
            const question = step.questions[questionIdx];
            if (step.stepNumber != 1) {
              this.getQuestionElement(step.stepNumber, question.number).clear();
              this.getQuestionElement(
                step.stepNumber,
                question.number
              ).isVisible = question.initialState;
            }
          }
        }
      }
    },
    parseFlag(value) {
      if (value === '1') {
        return 1;
      }

      if (value === '0') {
        return 0;
      }

      return null;
    },
    async mapPSAnswersToQuestions() {
      let pei = this.PeiGeneralDB;

      //paso año académico
      this.getQuestionElement(1, 1).selectedAnswer = this.SelectedPeiYear;
      this.getQuestionElement(1, 1).disabled = true;

      //Como se está actualizando datos de define PEI, esta pregunta es una validación operacional y no se guarda en la base de datos, por lo que siempre será Si en modo readOnly, porque el PEI ya está definido.
      this.getQuestionElement(1, 2).selectedAnswer = 1;
      this.getQuestionElement(1, 2).disabled = true;

      this.getQuestionElement(1, 2.1).isVisible = false;
      //paso detalle PS

      this.getQuestionElement(4, 6).selectedAnswer = pei.gradeId;
      this.getQuestionElement(4, 7).selectedAnswer = parseInt(
        pei.inapropriateConductFlag
      );
      this.getQuestionElement(4, 7.1).selectedAnswer = parseInt(
        pei.unilateralServiceRelatedFlag
      );

      this.getQuestionElement(3, 7).selectedAnswer = parseInt(
        pei.impedimentFlag
      );
      this.getQuestionElement(3, 7).disabled = true;

      this.getQuestionElement(3, 8).selectedAnswer = parseInt(
        pei.prescholarFlag
      );
      this.getQuestionElement(3, 8).disabled = true;

      this.getQuestionElement(3, 9).selectedAnswer = parseInt(
        pei.publicSchoolFlag
      );
      if (this.getQuestionElement(3, 9).selectedAnswer == 0) {
        await this.onQuestion9ValueChange(this.getQuestionElement(3, 9));

        this.getQuestionElement(3, 9).disabled = true;

        this.getQuestionElement(3, 9.1).selectedAnswer = parseInt(
          pei.peiRegularSpecialFlag
        );
        await this.onQuestion9dot1ValueChange(this.getQuestionElement(3, 9.1));
        this.getQuestionElement(3, 9.1).disabled = true;

        this.getQuestionElement(3, 9.2).selectedAnswer = parseInt(
          pei.configInstitutionalFlag
        );
        await this.onQuestion9dot2ValueChange(this.getQuestionElement(3, 9.2));
        this.getQuestionElement(3, 9.2).disabled = true;
      }

      //location
      let selectedRegion = null;
      let self = this;

      await self
        .getRegions()
        .then((res) => {
          selectedRegion = res.find((x) => x.Value == pei.locationRegionId);
          self.getQuestionField(4, 5.1, 'region').value = selectedRegion;
        })
        .catch((err) => {
          self.showErrorMessage(err);
        });

      if (selectedRegion != null) {
        await this.onRegionChange(selectedRegion);
        let selectedDistric = null;

        await self
          .getDistricts(self.SelectedPeiYear, selectedRegion.Value)
          .then((res) => {
            selectedDistric = res.find(
              (x) => x.Value == pei.configLocationDistrictId
            );
            self.getQuestionField(4, 5.1, 'distrito').value = selectedDistric;
          })
          .catch((err) => {
            self.showErrorMessage(err);
          });

        if (selectedDistric != null) {
          await this.onDistrictChange(selectedDistric);
          let isPublic = this.getQuestionElement(3, 9).selectedAnswer;
          let isSpecialSchool = this.getQuestionElement(3, 9.1).selectedAnswer;
          let isInstitution = this.getQuestionElement(3, 9.2).selectedAnswer;
          let selectedAnswerYearId = this.getQuestionElement(1, 1)
            .selectedAnswerId;
          let isUnilateral = 0; // Removed question that assigns this value and defaults to false

          let selectedSchool = null;
          await self
            .getSchools(
              selectedAnswerYearId,
              selectedDistric.Value,
              isUnilateral,
              isPublic,
              isSpecialSchool,
              isInstitution
            )
            .then((res) => {
              selectedSchool = res.find((x) => x.Value == pei.peiLocationId);
              self.getQuestionField(4, 5.1, 'escuela').value = selectedSchool;
            })
            .catch((err) => {
              self.showErrorMessage(err);
            });

          if (selectedSchool != null) {
            this.onSchoolChange(selectedSchool);
          }
        }
      }
    },
    showErrorMessage(error) {
      if (error && error.response && error.response.data) {
        this.ShowToast('', error.response.data.message, 'error');
      } else {
        this.ShowToast(
          '',
          'Ha ocurrido un error mientras se procesaba la información.',
          'error'
        );
      }
    },
    async mapPeiAnswersToQuestions() {
      let pei = this.PeiGeneralDB;

      let isDisabled = false;
      //paso año académico
      this.getQuestionElement(1, 1).selectedAnswer = this.SelectedPeiYear;
      await this.onQuestion1ValueChange(this.getQuestionElement(1, 1));
      this.getQuestionElement(1, 1).disabled = isDisabled;

      //Como se está actualizando datos de define PEI, esta pregunta es una validación operacional y no se guarda en la base de datos, por lo que siempre será Si en modo readOnly, porque el PEI ya está definido.
      this.getQuestionElement(1, 2).selectedAnswer = 1;
      this.getQuestionElement(1, 2).disabled = isDisabled;

      this.getQuestionElement(1, 2.1).isVisible = false;

      //paso preguntas adicionales---------------------------------------------------------------------------------------------------
      this.getStep(2).showStep = await this.hasSignedPeiLastYear;

      //alternativa de ubicacion
      let questionUbication = pei.peiAdditionalQuestions.find(
        (x) =>
          x.peiAdditionalQuestionId ==
          PeiAdditionalQuestionType.Evidencia_de_Participación_de_Alternatia_de_Ubicación
      );
      if (questionUbication != undefined) {
        this.getQuestionElement(2, 1).selectedAnswer =
          questionUbication.peiAdditionalQuestionAnswerId == EduEspYesNoNA.Yes
            ? 1
            : 0;
        await this.onQuestion2Step1ValueChange(this.getQuestionElement(2, 1));
        this.getQuestionElement(2, 1).disabled = isDisabled;

        if (!this.getQuestionElement(2, 1).selectedAnswer) {
          this.getQuestionElement(2, 1.1).selectedAnswer =
            questionUbication.alternatePlacementId;
          this.getQuestionElement(2, 1.2).questionOpenAnswer =
            questionUbication.additionalQuestionComment;
          this.getQuestionElement(2, 1.1).disabled = isDisabled;
          this.getQuestionElement(2, 1.2).disabled = isDisabled;
        }
      }

      //Validate for question P2 and P3
      await this.hasAgencyLastYearSignedPei();
      //post-secundario
      let questionPostSecundary = pei.peiAdditionalQuestions.find(
        (x) =>
          x.peiAdditionalQuestionId ==
          PeiAdditionalQuestionType.Evidencia_de_Participación_Servicio_Transición_Post_Secundario
      );
      if (questionPostSecundary != undefined) {
        if (questionPostSecundary.peiAdditionalQuestionAnswerId) {
          this.getQuestionElement(2, 2).selectedAnswer =
            questionPostSecundary.peiAdditionalQuestionAnswerId ==
            EduEspYesNoNA.Yes
              ? 1
              : 0;

          let question2Step2 = this.getQuestionElement(2, 2);

          if (question2Step2.visible || question2Step2.selectedAnswer == 1) {
            await this.onQuestion2Step2ValueChange(question2Step2);
          }
          this.getQuestionElement(2, 2).disabled = isDisabled;
          if (!this.getQuestionElement(2, 2).selectedAnswer) {
            this.getQuestionElement(2, 2.1).questionOpenAnswer =
              questionPostSecundary.additionalQuestionComment;
            this.getQuestionElement(2, 2.1).disabled = isDisabled;
          }
        } else {
          this.setQuestionVisible(2, 2, false);
        }
      }

      //Evidencia agencia
      let questionAgency = pei.peiAdditionalQuestions.find(
        (x) =>
          x.peiAdditionalQuestionId ==
          PeiAdditionalQuestionType.Evidencia_de_que_la_Agencia_Proveyó_el_Servicio
      );
      if (questionAgency != undefined) {
        if (questionAgency.peiAdditionalQuestionAnswerId) {
          this.getQuestionElement(2, 3).selectedAnswer =
            questionAgency.peiAdditionalQuestionAnswerId == EduEspYesNoNA.Yes
              ? 1
              : 0;
          await this.onQuestion3ValueChange(this.getQuestionElement(2, 3));
          this.getQuestionElement(2, 3).disabled = isDisabled;
          if (
            questionAgency.peiAdditionalQuestionAnswerId == EduEspYesNoNA.No &&
            questionAgency.additionalQuestionComment &&
            questionAgency.additionalQuestionComment.length > 0
          ) {
            this.getQuestionElement(2, 3.1).questionOpenAnswer =
              questionAgency.additionalQuestionComment;
            this.getQuestionElement(2, 3.1).disabled = isDisabled;
          } else {
            this.setQuestionVisible(2, 3.1, false);
          }
        } else {
          this.setQuestionVisible(2, 3, false);
        }
      }

      //asistente de servicio
      let questionService = pei.peiAdditionalQuestions.find(
        (x) =>
          x.peiAdditionalQuestionId ==
          PeiAdditionalQuestionType.Asistente_de_Servicio
      );

      if (questionService != undefined) {
        this.getQuestionElement(2, 4).selectedAnswer =
          questionService.peiAdditionalQuestionAnswerId == EduEspYesNoNA.Yes
            ? 1
            : 0;

        await this.onQuestion4ValueChange(this.getQuestionElement(2, 4));
        this.getQuestionElement(2, 4).disabled = isDisabled;
        if (this.getQuestionElement(2, 4).selectedAnswer) {
          this.getQuestionElement(2, 4.2).selectedAnswer =
            questionService.serviceAssistantServiceId;
          this.getQuestionElement(2, 4.2).disabled = isDisabled;
        }
      } else {
        this.getQuestionElement(2, 4).selectedAnswer = false;
      }

      let questionUniqueService = pei.peiAdditionalQuestions.find(
        (x) =>
          x.peiAdditionalQuestionId ==
          PeiAdditionalQuestionType.Asistente_de_Servicio_Único
      );
      if (questionUniqueService != undefined) {
        this.getQuestionElement(2, 4.1).selectedAnswer =
          questionUniqueService.peiAdditionalQuestionAnswerId ==
          EduEspYesNoNA.Yes
            ? 1
            : 0;
        await this.onQuestion4AValueChange(this.getQuestionElement(2, 4.1));
        this.getQuestionElement(2, 4.1).disabled = isDisabled;
        if (this.getQuestionElement(2, 4.1).selectedAnswer) {
          this.getQuestionElement(2, 4.3).questionOpenAnswer =
            questionUniqueService.serviceAssistantName;
          this.getQuestionElement(2, 4.3).disabled = isDisabled;
        }
      }

      //fecha ubicacion
      let questionUbicationDate = pei.peiAdditionalQuestions.find(
        (x) =>
          x.peiAdditionalQuestionId ==
          PeiAdditionalQuestionType.Fecha_de_Ubicación
      );
      if (questionUbicationDate != undefined) {
        this.getQuestionElement(2, 5).isVisible = true;
        this.getQuestionElement(2, 5).selectedAnswer =
          questionUbicationDate.alternatePlacementDate;
        this.getQuestionElement(2, 5).disabled = isDisabled;
      }

      //Evidencia movilidad y accesos
      let question6 = this.getQuestionElement(2, 6);
      let questionMovilityAccess = pei.peiAdditionalQuestions.find(
        (x) =>
          x.peiAdditionalQuestionId ==
          PeiAdditionalQuestionType.Evidencia_movilidad_acceso
      );

      if (questionMovilityAccess != undefined) {
        question6.selectedAnswer =
          questionMovilityAccess.peiAdditionalQuestionAnswerId ==
          EduEspYesNoNA.Yes
            ? 1
            : 0;
        await this.onQuestion6ValueChange(question6);

        if (question6.selectedAnswer) {
          //busca las selecciones
          questionMovilityAccess.multipleAnswer.forEach((s) => {
            question6.getField('options').selectedOptions.push(s.id);
          });

          //pregunta 6.1
          let question6dot1 = this.getQuestionElement(2, 6.1);
          let expressedNeeds = pei.peiAdditionalQuestions.find(
            (x) =>
              x.peiAdditionalQuestionId ==
              PeiAdditionalQuestionType.Necesidades_expresadas
          );
          if (expressedNeeds != undefined) {
            question6dot1.selectedAnswer =
              expressedNeeds.peiAdditionalQuestionAnswerId == EduEspYesNoNA.Yes
                ? 1
                : 0;
            await this.onQuestion6AValueChange(question6dot1);

            if (!question6dot1.selectedAnswer) {
              question6dot1.getField('comentario').value =
                expressedNeeds.additionalQuestionComment;
            }

            if (question6dot1.selectedAnswer) {
              //pregunta 6.2
              let question6dot2 = this.getQuestionElement(2, 6.2);
              let adjustmentRequired = pei.peiAdditionalQuestions.find(
                (x) =>
                  x.peiAdditionalQuestionId ==
                  PeiAdditionalQuestionType.Ajuste_organizacion_escolar
              );
              if (adjustmentRequired != undefined) {
                question6dot2.selectedAnswer =
                  adjustmentRequired.peiAdditionalQuestionAnswerId ==
                  EduEspYesNoNA.Yes
                    ? 1
                    : 0;
                await this.onQuestion6BValueChange(question6dot2);

                if (!question6dot2.selectedAnswer) {
                  question6dot2.getField('comentario').value =
                    adjustmentRequired.additionalQuestionComment;
                }

                if (question6dot2.selectedAnswer) {
                  //pregunta 6.3
                  let question6dot3 = this.getQuestionElement(2, 6.3);
                  let adjustmentProvided = pei.peiAdditionalQuestions.find(
                    (x) =>
                      x.peiAdditionalQuestionId ==
                      PeiAdditionalQuestionType.Proveyó_ajuste
                  );
                  if (adjustmentProvided != undefined) {
                    question6dot3.selectedAnswer =
                      adjustmentProvided.peiAdditionalQuestionAnswerId ==
                      EduEspYesNoNA.Yes
                        ? 1
                        : 0;
                    await this.onQuestion6CValueChange(question6dot3);

                    if (!question6dot3.selectedAnswer) {
                      question6dot3.getField('comentario').value =
                        adjustmentProvided.additionalQuestionComment;
                    }

                    if (question6dot3.selectedAnswer) {
                      //pregunta 6.4
                      let question6dot4 = this.getQuestionElement(2, 6.4);
                      let adjsutmentType = pei.peiAdditionalQuestions.find(
                        (x) =>
                          x.peiAdditionalQuestionId ==
                          PeiAdditionalQuestionType.Tipo_ajuste
                      );
                      if (adjsutmentType != undefined) {
                        adjsutmentType.multipleAnswer.forEach((s) => {
                          question6dot4
                            .getField('tipoajustes')
                            .selectedOptions.push(s.id);
                          if (s.id == 14) {
                            question6dot4.getField('comentario').value =
                              s.value;
                            question6dot4.getField('comentario').visible = true;
                          }
                        });
                      }

                      //pregunta 6.5
                      let question6dot5 = this.getQuestionElement(2, 6.5);
                      let integratedEnvironmentAdjustment = pei.peiAdditionalQuestions.find(
                        (x) =>
                          x.peiAdditionalQuestionId ==
                          PeiAdditionalQuestionType.Ajuste_ambiente_integrado
                      );
                      if (integratedEnvironmentAdjustment != undefined) {
                        question6dot5.selectedAnswer =
                          integratedEnvironmentAdjustment.peiAdditionalQuestionAnswerId ==
                          EduEspYesNoNA.Yes
                            ? 1
                            : 0;
                      }

                      //pregunta 6.6
                      let question6dot6 = this.getQuestionElement(2, 6.6);
                      let AdjustmentTerms = pei.peiAdditionalQuestions.find(
                        (x) =>
                          x.peiAdditionalQuestionId ==
                          PeiAdditionalQuestionType.Ajuste_en_termino_5_dias
                      );
                      if (AdjustmentTerms != undefined) {
                        question6dot6.selectedAnswer =
                          AdjustmentTerms.peiAdditionalQuestionAnswerId ==
                          EduEspYesNoNA.Yes
                            ? 1
                            : 0;
                        await this.onQuestion6FValueChange(question6dot6);
                      }

                      if (!question6dot6.selectedAnswer) {
                        question6dot6.getField('comentario').value =
                          AdjustmentTerms.additionalQuestionComment;
                      }
                    }
                  }
                }
              }
            }
          }

          //pregunta 6.7
          let question6dot7 = this.getQuestionElement(2, 6.7);
          let AdjustmentMovility = pei.peiAdditionalQuestions.find(
            (x) =>
              x.peiAdditionalQuestionId ==
              PeiAdditionalQuestionType.Necesidad_Movilidad
          );
          if (AdjustmentMovility != undefined) {
            question6dot7.selectedAnswer =
              AdjustmentMovility.peiAdditionalQuestionAnswerId ==
              EduEspYesNoNA.Yes
                ? 1
                : 0;

            if (!question6dot7.selectedAnswer) {
              let fieldComment2 = this.getQuestionElement(2, 6.7).getField(
                'comentario'
              );
              fieldComment2.visible = true;
              question6dot7.getField('comentario').value =
                AdjustmentMovility.additionalQuestionComment;
            }
          }
        }
      }

      //Condiciones adicionales durante el proceso determinación elegibilidad
      let question7 = this.getQuestionElement(2, 7);
      let questionEligibilityConditions = pei.peiAdditionalQuestions.find(
        (x) =>
          x.peiAdditionalQuestionId ==
          PeiAdditionalQuestionType.Condiciones_Adicionales_Durante_El_Proceso_Determinacion_Elegibilidad
      );

      if (questionEligibilityConditions == undefined && this.isReadOnly) {
        question7.selectedAnswer = 0;
      }
      if (questionEligibilityConditions != undefined) {
        question7.selectedAnswer =
          questionEligibilityConditions.peiAdditionalQuestionAnswerId ==
          EduEspYesNoNA.Yes
            ? 1
            : 0;

        if (
          questionEligibilityConditions.peiAdditionalQuestionAnswerId ==
          EduEspYesNoNA.Yes
        ) {
          this.showElegibilityDeterminationConditions = true;

          const { data } = await this.$ApiGet(
            `/api/pei/eligibilityconditions/${pei.peiId}`
          );

          data.forEach((x) => {
            let condition = this.determinationElegibilityOptions.find(
              (c) => c.id == x
            );

            //Set Item
            let item = {};
            item.id = condition.id;
            item.association = null;
            item.title = condition.name;
            item.code = condition.code;
            this.peiEligibilityDeterminationData.eligibilityDeterminationConditions.selectedItems.push(
              item
            );
          });

          this.peiEligibilityDeterminationData.eligibilityDeterminationConditions.selectedItems.forEach(
            (item) => {
              this.determinationElegibilityOptions = this.determinationElegibilityOptions.filter(
                (x) => x.id !== item.id
              );
            }
          );
        }
      }

      //paso Inicio
      this.getQuestionElement(3, 7).selectedAnswer = this.parseFlag(
        pei.impedimentFlag
      );
      this.getQuestionElement(3, 7).disabled = isDisabled;

      this.getQuestionElement(3, 8).selectedAnswer = this.parseFlag(
        pei.prescholarFlag
      );
      this.getQuestionElement(3, 8).disabled = isDisabled;

      this.getQuestionElement(3, 9).selectedAnswer = this.parseFlag(
        pei.publicSchoolFlag
      );
      this.getQuestionElement(3, 9).disabled = isDisabled;
      if (this.getQuestionElement(3, 9).selectedAnswer == 0) {
        await this.onQuestion9ValueChange(this.getQuestionElement(3, 9));

        this.getQuestionElement(3, 9).disabled = isDisabled;

        this.getQuestionElement(3, 9.1).selectedAnswer = this.parseFlag(
          pei.peiRegularSpecialFlag
        );
        await this.onQuestion9dot1ValueChange(this.getQuestionElement(3, 9.1));
        this.getQuestionElement(3, 9.1).disabled = isDisabled;

        this.getQuestionElement(3, 9.2).selectedAnswer = this.parseFlag(
          pei.configInstitutionalFlag
        );
        if (!this.getQuestionElement(3, 9.1).selectedAnswer) {
          await this.onQuestion9dot2ValueChange(
            this.getQuestionElement(3, 9.2)
          );
          this.getQuestionElement(3, 9.2).disabled = isDisabled;
        }
      }
      //paso detalle (No disabled)
      this.getQuestionElement(4, 6).selectedAnswer = pei.gradeId;
      const canShow = parseInt(pei.impedimentCognativeFlag) == 1;

      this.getQuestionElement(4, 6).isDisabled =
        parseInt(pei.impedimentCognativeFlag) == 1;

      this.getQuestionElement(4, 7).selectedAnswer = parseInt(
        pei.inapropriateConductFlag
      );
      this.getQuestionElement(4, 8).selectedAnswer = parseInt(
        pei.transitionServicesFlag
      );
      if (this.getQuestionElement(4, 8).selectedAnswer == 1)
        await this.onQuestion8Step4ValueChange(this.getQuestionElement(4, 8));

      this.getQuestionElement(4, 8.1).selectedAnswer = pei.peiTransitionCode;
      this.getQuestionElement(4, 9).selectedAnswer = parseInt(
        pei.impedimentCognativeFlag
      );

      this.getQuestionElement(4, 10).selectedAnswer = parseInt(
        pei.placementFlag
      );
      this.getQuestionElement(4, 11).selectedAnswer = parseInt(
        pei.peiOtherServMeasureCode
      );
      this.getQuestionElement(4, 12).selectedAnswer = parseInt(
        pei.otherServTechAssistEquipFlag
      );
      this.getQuestionElement(4, 13).selectedAnswer = parseInt(
        pei.extendedSchoolYearFlag
      );
      this.getQuestionElement(4, 14).selectedAnswer = parseInt(
        pei.configMobilityProblemFlag
      );
      this.getQuestionElement(4, 15).selectedAnswer = parseInt(
        pei.configSignsLanguageFlag
      );
      this.getQuestionElement(4, 16).selectedAnswer = parseInt(
        pei.configBrailleFlag
      );

      await this.onQuestion16Step4ValueChange(this.getQuestionElement(4, 16));

      var readingModes = await this.codeReadingMode();

      var primaryReadingMode = readingModes.filter(
        (t) => t.Value == pei.primaryReadingModeId
      );

      var secondaryReadingMode = readingModes.filter(
        (t) => t.Value == pei.secondaryReadingModeId
      );

      this.getQuestionElement(4, 17).getField('Primario').value =
        primaryReadingMode.length > 0 ? primaryReadingMode[0] : null;

      this.getQuestionElement(4, 17).getField('Secundario').value =
        secondaryReadingMode.length > 0 ? secondaryReadingMode[0] : null;

      this.getQuestionElement(4, 18).selectedAnswer = pei.readingLevelId;

      if (pei.configSignsLanguageFlag == 1) {
        let question19 = this.getQuestionElement(4, 19);
        question19.isVisible = true;
        question19.isRequired = true;
        question19.refreshOptions();
        question19.selectedAnswer = pei.signLanguageProficiencyLevelId;
      }

      if (pei.peiOtherServMeasureCode == 16) {
        this.getQuestionElement(4, 11.1).selectedAnswer = parseInt(
          pei.otherServMeasurePlacementFlag
        );
        await this.onQuestion11Step4ValueChange(this.getQuestionElement(4, 11));
      }

      this.getQuestionElement(4, 20).selectedAnswer =
        pei.placedInModifiedClassroomFlag;

      let self = this;
      //location
      let selectedRegion = null;
      await self
        .getRegions()
        .then((res) => {
          selectedRegion = res.find((x) => x.Value == pei.regionId);
          if (pei.publicSchoolFlag == '0') {
            selectedRegion = res.find((x) => x.Value == pei.locationRegionId);
          }
          self.getQuestionField(4, 5.1, 'region').value = selectedRegion;
        })
        .catch((err) => {
          self.showErrorMessage(err);
        });

      if (selectedRegion != null) {
        await this.onRegionChange(selectedRegion);
        let selectedDistric = null;
        await this.getDistricts(self.SelectedPeiYear, selectedRegion.Value)
          .then((res) => {
            selectedDistric = res.find((x) => x.Value == pei.districtId);
            if (pei.publicSchoolFlag == '0') {
              selectedDistric = res.find(
                (x) => x.Value == pei.configLocationDistrictId
              );
            }
            self.getQuestionField(4, 5.1, 'distrito').value = selectedDistric;
          })
          .catch((err) => {
            self.showErrorMessage(err);
          });

        if (selectedDistric != null) {
          await this.onDistrictChange(selectedDistric);
          let isPublic = this.getQuestionElement(3, 9).selectedAnswer;
          let isSpecialSchool = this.getQuestionElement(3, 9.1).selectedAnswer;
          let isInstitution = this.getQuestionElement(3, 9.2).selectedAnswer;
          let selectedAnswerYearId = this.getQuestionElement(1, 1)
            .selectedAnswerId;
          let isUnilateral = 0; // Removed question that assigns this value and defaults to false

          let selectedSchool = {};
          await self
            .getSchools(
              selectedAnswerYearId,
              selectedDistric.Value,
              isUnilateral,
              isPublic,
              isSpecialSchool,
              isInstitution
            )
            .then((res) => {
              selectedSchool = res.find((x) => x.Value == pei.schoolId);
              if (pei.publicSchoolFlag == '0') {
                selectedSchool = res.find((x) => x.Value == pei.peiLocationId);
              }
              self.getQuestionField(4, 5.1, 'escuela').value = selectedSchool;
            })
            .catch((err) => {
              self.showErrorMessage(err);
            });

          if (selectedSchool != null) {
            await this.onSchoolChange(selectedSchool);
          }
        }
      }
    },
    mapQuestionsAnswers() {
      let pei = this.PeiGeneral;
      pei.StudentSieId = parseInt(this.studentSieId);
      pei.PeiAdditionalQuestions = [];
      try {
        pei.ConfigAcademicYearId = this.getQuestionElement(1, 1).selectedAnswer;
        pei.IsRevision = this.getQuestionElement(1, 2).selectedAnswer ? 1 : 0;

        if (pei.IsRevision == 1) {
          pei.PeiTypeCode = peiTypes.Revision;
        } else {
          pei.PeiTypeCode = peiTypes.Initial;
        }

        pei.ConfigAgeAmt = this.$store.getters.SelectedStudent.age;
        //Unilateral siempre será false al crear PEI desde el wizard
        pei.IsUnilateral = 0;

        //paso preguntas adicionales---------------------------------------------------------------------------------------------------
        //alternativa de ubicacion
        let question1 = this.getQuestionElement(2, 1);
        let paPlacement = this.PeiAdditionalQuestion;
        paPlacement.PeiAdditionalQuestionId =
          PeiAdditionalQuestionType.Evidencia_de_Participación_de_Alternatia_de_Ubicación;
        paPlacement.PeiAdditionalQuestionAnswerId = question1.selectedAnswer
          ? EduEspYesNoNA.Yes
          : EduEspYesNoNA.No;

        if (!question1.selectedAnswer) {
          paPlacement.AlternatePlacementId = this.getQuestionElement(
            2,
            1.1
          ).selectedAnswer;
          paPlacement.AdditionalQuestionComment = this.getQuestionElement(
            2,
            1.2
          ).questionOpenAnswer;
        }

        if (question1.selectedAnswer != null) {
          pei.PeiAdditionalQuestions.push(paPlacement);
        }

        //post-secundario
        let question2 = this.getQuestionElement(2, 2);
        this.PeiAdditionalQuestion = {};
        let paPostSecundario = this.PeiAdditionalQuestion;
        paPostSecundario.PeiAdditionalQuestionId =
          PeiAdditionalQuestionType.Evidencia_de_Participación_Servicio_Transición_Post_Secundario;
        paPostSecundario.PeiAdditionalQuestionAnswerId = question2.selectedAnswer
          ? EduEspYesNoNA.Yes
          : EduEspYesNoNA.No;

        if (!question2.selectedAnswer) {
          paPostSecundario.AdditionalQuestionComment = this.getQuestionElement(
            2,
            2.1
          ).questionOpenAnswer;
        }

        if (question2.selectedAnswer != null) {
          pei.PeiAdditionalQuestions.push(paPostSecundario);
        }

        //agencia
        let question3 = this.getQuestionElement(2, 3);
        this.PeiAdditionalQuestion = {};
        let paAgencyParticipation = this.PeiAdditionalQuestion;
        paAgencyParticipation.PeiAdditionalQuestionId =
          PeiAdditionalQuestionType.Evidencia_de_que_la_Agencia_Proveyó_el_Servicio;
        paAgencyParticipation.PeiAdditionalQuestionAnswerId = question3.selectedAnswer
          ? EduEspYesNoNA.Yes
          : EduEspYesNoNA.No;

        if (!question3.selectedAnswer) {
          paAgencyParticipation.AdditionalQuestionComment = this.getQuestionElement(
            2,
            3.1
          ).questionOpenAnswer;
        }

        if (question3.selectedAnswer != null) {
          pei.PeiAdditionalQuestions.push(paAgencyParticipation);
        }

        //asistente de servicio
        let question4 = this.getQuestionElement(2, 4);
        this.PeiAdditionalQuestion = {};
        let paServiceAssistant = this.PeiAdditionalQuestion;
        paServiceAssistant.PeiAdditionalQuestionId =
          PeiAdditionalQuestionType.Asistente_de_Servicio;
        paServiceAssistant.PeiAdditionalQuestionAnswerId = question4.selectedAnswer
          ? EduEspYesNoNA.Yes
          : EduEspYesNoNA.No;

        if (question4.selectedAnswer) {
          paServiceAssistant.ServiceAssistantServiceId = this.getQuestionElement(
            2,
            4.2
          ).selectedAnswer;
        }

        if (question4.selectedAnswer != null) {
          pei.PeiAdditionalQuestions.push(paServiceAssistant);
        }

        let question4a = this.getQuestionElement(2, 4.1);
        this.PeiAdditionalQuestion = {};
        let paServiceAssistantSingleStudent = this.PeiAdditionalQuestion;
        paServiceAssistantSingleStudent.PeiAdditionalQuestionId =
          PeiAdditionalQuestionType.Asistente_de_Servicio_Único;
        paServiceAssistantSingleStudent.PeiAdditionalQuestionAnswerId = question4a.selectedAnswer
          ? EduEspYesNoNA.Yes
          : EduEspYesNoNA.No;

        if (question4a.selectedAnswer) {
          paServiceAssistantSingleStudent.ServiceAssistantName = this.getQuestionElement(
            2,
            4.3
          ).questionOpenAnswer;
        }

        pei.PeiAdditionalQuestions.push(paServiceAssistantSingleStudent);

        //fecha ubicacion
        let question5 = this.getQuestionElement(2, 5);
        if (question5.selectedAnswer) {
          this.PeiAdditionalQuestion = {};
          let paAlternatePlacementDate = this.PeiAdditionalQuestion;
          paAlternatePlacementDate.PeiAdditionalQuestionId =
            PeiAdditionalQuestionType.Fecha_de_Ubicación;
          paAlternatePlacementDate.PeiAdditionalQuestionAnswerId = question4a.selectedAnswer
            ? EduEspYesNoNA.Yes
            : EduEspYesNoNA.No;
          paAlternatePlacementDate.AlternatePlacementDate =
            question5.selectedAnswer;
          pei.AlternatePlacementDate =
            paAlternatePlacementDate.AlternatePlacementDate;

          if (question5.selectedAnswer != null) {
            pei.PeiAdditionalQuestions.push(paAlternatePlacementDate);
          }
        }

        //movilidad y acceso - pregunta 6 - barreras arquitectónicas - yesorNot and multiselect
        let question6 = this.getQuestionElement(2, 6);
        let accessMovilityEvidence = {};
        accessMovilityEvidence.PeiAdditionalQuestionId =
          PeiAdditionalQuestionType.Evidencia_movilidad_acceso;
        accessMovilityEvidence.PeiAdditionalQuestionAnswerId = question6.selectedAnswer
          ? EduEspYesNoNA.Yes
          : EduEspYesNoNA.No;
        if (question6.selectedAnswer) {
          //multiselected answers for question 6
          accessMovilityEvidence.MultipleAnswer = [];
          question6.getField('options').selectedOptions.forEach((element) => {
            accessMovilityEvidence.MultipleAnswer.push({
              id: element,
              value: null,
            });
          });
          accessMovilityEvidence.IsMultiple = true;
        }

        if (question6.selectedAnswer != null) {
          pei.PeiAdditionalQuestions.push(accessMovilityEvidence);
        }

        if (question6.selectedAnswer) {
          //pregunta 6.1
          let question6Dot1 = this.getQuestionElement(2, 6.1);
          let expressedNeeds = {};
          expressedNeeds.PeiAdditionalQuestionId =
            PeiAdditionalQuestionType.Necesidades_expresadas;
          expressedNeeds.PeiAdditionalQuestionAnswerId = question6Dot1.selectedAnswer
            ? EduEspYesNoNA.Yes
            : EduEspYesNoNA.No;
          if (!question6Dot1.selectedAnswer) {
            expressedNeeds.AdditionalQuestionComment = question6Dot1.getField(
              'comentario'
            ).value;
          }
          pei.PeiAdditionalQuestions.push(expressedNeeds);

          //pregunta 6.7
          let question6Dot6Dot1 = this.getQuestionElement(2, 6.7);
          if (question6Dot6Dot1) {
            let AdjustmentMovility = {};
            AdjustmentMovility.PeiAdditionalQuestionId =
              PeiAdditionalQuestionType.Necesidad_Movilidad;
            AdjustmentMovility.PeiAdditionalQuestionAnswerId = question6Dot6Dot1.selectedAnswer
              ? EduEspYesNoNA.Yes
              : EduEspYesNoNA.No;
            AdjustmentMovility.AdditionalQuestionComment = question6Dot6Dot1.getField(
              'comentario'
            )?.value;
            pei.PeiAdditionalQuestions.push(AdjustmentMovility);
          }

          if (question6Dot1.selectedAnswer) {
            //pregunta 6.2
            let question6Dot2 = this.getQuestionElement(2, 6.2);
            let adjustScholarNeeds = {};
            adjustScholarNeeds.PeiAdditionalQuestionId =
              PeiAdditionalQuestionType.Ajuste_organizacion_escolar;
            adjustScholarNeeds.PeiAdditionalQuestionAnswerId = question6Dot2.selectedAnswer
              ? EduEspYesNoNA.Yes
              : EduEspYesNoNA.No;
            if (!question6Dot2.selectedAnswer) {
              adjustScholarNeeds.AdditionalQuestionComment = question6Dot2.getField(
                'comentario'
              ).value;
            }
            pei.PeiAdditionalQuestions.push(adjustScholarNeeds);

            if (question6Dot2.selectedAnswer) {
              //pregunta 6.3
              let question6Dot3 = this.getQuestionElement(2, 6.3);
              let adjustmentProvided = {};
              adjustmentProvided.PeiAdditionalQuestionId =
                PeiAdditionalQuestionType.Proveyó_ajuste;
              adjustmentProvided.PeiAdditionalQuestionAnswerId = question6Dot3.selectedAnswer
                ? EduEspYesNoNA.Yes
                : EduEspYesNoNA.No;
              if (!question6Dot3.selectedAnswer) {
                adjustmentProvided.AdditionalQuestionComment = question6Dot3.getField(
                  'comentario'
                ).value;
              }
              pei.PeiAdditionalQuestions.push(adjustmentProvided);

              if (question6Dot3.selectedAnswer) {
                //pregunta 6.4
                let question6Dot4 = this.getQuestionElement(2, 6.4);
                let adjustmentType = {};
                adjustmentType.PeiAdditionalQuestionId =
                  PeiAdditionalQuestionType.Tipo_ajuste;
                adjustmentType.PeiAdditionalQuestionAnswerId =
                  EduEspYesNoNA.Yes;
                adjustmentType.MultipleAnswer = [];
                question6Dot4
                  .getField('tipoajustes')
                  .selectedOptions.forEach((element) => {
                    adjustmentType.MultipleAnswer.push({
                      id: element,
                      value:
                        element == 14
                          ? question6Dot4.getField('comentario').value
                          : null,
                    });
                  });
                adjustmentType.IsMultiple = true;
                pei.PeiAdditionalQuestions.push(adjustmentType);

                //pregunta 6.5
                let question6Dot5 = this.getQuestionElement(2, 6.5);
                let integratedEnvironmentAdjustment = {};
                integratedEnvironmentAdjustment.PeiAdditionalQuestionId =
                  PeiAdditionalQuestionType.Ajuste_ambiente_integrado;
                integratedEnvironmentAdjustment.PeiAdditionalQuestionAnswerId = question6Dot5.selectedAnswer
                  ? EduEspYesNoNA.Yes
                  : EduEspYesNoNA.No;
                pei.PeiAdditionalQuestions.push(
                  integratedEnvironmentAdjustment
                );

                //pregunta 6.6
                let question6Dot6 = this.getQuestionElement(2, 6.6);
                let AdjustmentTerms = {};
                AdjustmentTerms.PeiAdditionalQuestionId =
                  PeiAdditionalQuestionType.Ajuste_en_termino_5_dias;
                AdjustmentTerms.PeiAdditionalQuestionAnswerId = question6Dot6.selectedAnswer
                  ? EduEspYesNoNA.Yes
                  : EduEspYesNoNA.No;
                if (!question6Dot6.selectedAnswer) {
                  AdjustmentTerms.AdditionalQuestionComment = question6Dot6.getField(
                    'comentario'
                  ).value;
                }
                pei.PeiAdditionalQuestions.push(AdjustmentTerms);
              }
            }
          }
        }

        //Condiciones adicionales durante el proceso determinación elegibilidad
        let question7 = this.getQuestionElement(2, 7);
        this.PeiAdditionalQuestion = {};
        let paEligibilityConditions = this.PeiAdditionalQuestion;
        paEligibilityConditions.PeiAdditionalQuestionId =
          PeiAdditionalQuestionType.Condiciones_Adicionales_Durante_El_Proceso_Determinacion_Elegibilidad;
        paEligibilityConditions.PeiAdditionalQuestionAnswerId = question7.selectedAnswer
          ? EduEspYesNoNA.Yes
          : EduEspYesNoNA.No;

        if (question7.selectedAnswer != null) {
          pei.PeiAdditionalQuestions.push(paEligibilityConditions);

          if (
            paEligibilityConditions.PeiAdditionalQuestionAnswerId ==
            EduEspYesNoNA.Yes
          ) {
            pei.eligibilityDeterminationConditions = this.peiEligibilityDeterminationData.eligibilityDeterminationConditions.selectedItems.map(
              (c) => c.id
            );
          }
        }

        //FIN paso preguntas adicionales-------------------------------------------------------------------------------------------------
        //paso Inicio
        pei.ConfigImpedimentFlag = this.getQuestionElement(3, 7).selectedAnswer
          ? 1
          : 0;
        pei.ConfigPrescholarFlag = this.getQuestionElement(3, 8).selectedAnswer
          ? 1
          : 0;
        pei.ConfigAgeAmtFlag = pei.ConfigPrescholarFlag;
        pei.ConfigPublicSchoolFlag = this.getQuestionElement(3, 9)
          .selectedAnswer
          ? 1
          : 0;
        pei.IsSpecialPrivateStudent = this.getQuestionElement(3, 9.1)
          .selectedAnswer
          ? 1
          : 0;
        pei.IsInstitutional = this.getQuestionElement(3, 9.2).selectedAnswer
          ? 1
          : 0;

        //Unilateral will always be false when creating PEI from the wizard
        pei.ConfigUnilateralFlag = 0;

        //paso detalle
        let question6step4 = this.getQuestionElement(4, 6);
        pei.ConfigGradeId = question6step4.selectedAnswer;
        pei.ConfigInapropriateConductFlag = this.getQuestionElement(4, 7)
          .selectedAnswer
          ? 1
          : 0;
        pei.ConfigTransitionServicesFlag = this.getQuestionElement(4, 8)
          .selectedAnswer
          ? 1
          : 0;
        pei.PeiTransitionCode = this.getQuestionElement(4, 8.1).selectedAnswer;
        pei.ConfigImpedimentCognativeFlag = this.getQuestionElement(4, 9)
          .selectedAnswer
          ? 1
          : 0;
        pei.ConfigPlacementFlag = this.getQuestionElement(4, 10).selectedAnswer
          ? 1
          : 0;
        pei.PeiOtherServMeasureCode = this.getQuestionElement(
          4,
          11
        ).selectedAnswer;
        pei.ConfigOtherServTechAssistEquipFlag = this.getQuestionElement(4, 12)
          .selectedAnswer
          ? 1
          : 0;
        pei.ConfigExtendedSchoolYearFlag = this.getQuestionElement(4, 13)
          .selectedAnswer
          ? 1
          : 0;
        pei.ConfigMobilityProblemFlag = this.getQuestionElement(4, 14)
          .selectedAnswer
          ? 1
          : 0;
        pei.ConfigSignsLanguageFlag = this.getQuestionElement(4, 15)
          .selectedAnswer
          ? 1
          : 0;
        pei.ConfigBrailleFlag = this.getQuestionElement(4, 16).selectedAnswer
          ? 1
          : 0;

        pei.PrimaryReadingModeId = this.getQuestionElement(4, 17).getField(
          'Primario'
        )?.value?.Value;

        pei.SecondaryReadingModeId = this.getQuestionElement(4, 17).getField(
          'Secundario'
        )?.value?.Value;

        pei.ReadingLevelId = this.getQuestionElement(4, 18).selectedAnswer;

        pei.SignLanguageProficiencyLevelId = null;

        if (pei.ConfigSignsLanguageFlag) {
          pei.SignLanguageProficiencyLevelId = this.getQuestionElement(
            4,
            19
          ).selectedAnswer;
        }

        //ubicación

        if (pei.IsInstitutional || pei.IsSpecialPrivateStudent) {
          pei.ConfigLocationRegionId = this.getQuestionField(
            4,
            5.1,
            'region'
          ).value.Value;
          pei.ConfigLocationDistrictId = this.getQuestionField(
            4,
            5.1,
            'distrito'
          ).value.Value;
          pei.GeneralStudentPublicSchoolFlag = 0;
        } else {
          pei.RegionId = this.getQuestionField(4, 5.1, 'region').value.Value;
          pei.DistrictId = this.getQuestionField(
            4,
            5.1,
            'distrito'
          ).value.Value;
          pei.GeneralStudentPublicSchoolFlag = 1;
        }

        if (pei.IsSpecialPrivateStudent || pei.IsInstitutional) {
          pei.PeiLocationId = this.getQuestionField(
            4,
            5.1,
            'escuela'
          ).value.Value;
        } else {
          pei.ConfigSchoolId = this.getQuestionField(
            4,
            5.1,
            'escuela'
          ).value.Value;
        }

        pei.PeiUnilateralLocationId = null;
        pei.ConfigUnilateralServiceEducationalFlag = 0;
        pei.ConfigUnilateralServiceRelatedFlag = 0;

        if (!pei.ConfigTransitionServicesFlag) {
          pei.PeiTransitionCode = null;
        }

        if (pei.PeiOtherServMeasureCode != 16) {
          pei.ConfigOtherServMeasurePlacementFlag = 0;
        } else {
          pei.ConfigOtherServMeasurePlacementFlag = this.getQuestionElement(
            4,
            11.1
          ).selectedAnswer
            ? 1
            : 0;
        }

        //si estamos completando un PS se asigna lo respondido en la pregunta
        if (this.isFromPeiDetail) {
          pei.ConfigUnilateralServiceRelatedFlag = this.getQuestionElement(
            4,
            7.1
          ).selectedAnswer
            ? 1
            : 0;
        }

        pei.PlacedInModifiedClassroomFlag = this.getQuestionElement(4, 20)
          .selectedAnswer
          ? 1
          : 0;

        this.PeiGeneral = pei;
      } catch (error) {
        Swal.mixin({
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 6000,
        }).fire(
          error,
          'Ha ocurrido un error mientras se procesaba la información.',
          'error'
        );
      }
    },
    async createPei() {
      try {
        // US94485: formdata is created because in previous versions the document was sent for data set creation
        let formData = ObjectToFormData(this.PeiGeneral, { indices: true });

        let res = await this.$ApiPost('/api/pei/create', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (res && res.data != null) {
          //redirigir a detalle de PEI
          this.$store.commit(
            'setSelectedPeiConfigUnilateralFlag',
            res.data.unilateralFlag
          );
          this.setPeiId(res.data.peiId);
          this.setLocationRejected(res.data.configLocationRejectFlag);
          this.SynchronizeAnalisisServices(res.data.peiId);

          //Solo Pei Regular y PS van a poder acceder a enmienda
          if (
            res.data.generalRevisionNo > 0 &&
            ((res.data.unilateralFlag == '0' &&
              res.data.configLocationRejectFlag == '0') ||
              (res.data.unilateralFlag == '1' &&
                res.data.configLocationRejectFlag == '0'))
          ) {
            this.$router.push({ path: '/pei/amendment' });
          } else {
            this.$router.push({ path: '/pei/detail' });
          }
        }
      } catch (err) {
        Swal.mixin({
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 6000,
        }).fire(
          err.message,
          'Ha ocurrido un error mientras se procesaba la información.',
          'error'
        );
      }
    },
    async updatePei() {
      let route = '/api/pei/updatePeiDetailsStep';
      if (this.PeiGeneralDB.unilateralFlag == '1') {
        route = '/api/pei/updatePsDetailsStep';
      }
      this.PeiGeneral.peiId = this.$store.getters.SelectedPei;
      return await new Promise((resolve) => {
        this.$ApiPut(route, this.PeiGeneral)
          .then((res) => {
            if (res && res.data != null) {
              resolve(res.data);

              //redirigir a detalle de PEI
              this.setPeiId(res.data.peiId);
              this.$router.push({ path: '/pei/detail' });
            }
          })
          .catch(function(error) {
            Swal.mixin({
              toast: true,
              position: 'top-end',
              showConfirmButton: false,
              timer: 6000,
            }).fire(
              error,
              'Ha ocurrido un error mientras se procesaba la información.',
              'error'
            );
          });
      });
    },
    SynchronizeAnalisisServices(peiId) {
      this.$ApiPost('api/pei/SynchronizeAnalisisServices/' + peiId);
    },
    setPeiId(peiId) {
      this.$store.dispatch('setSelectedPei', peiId);
    },
    setLocationRejected(locationRejected) {
      this.$store.dispatch('setSelectedPeiLocationRejected', locationRejected);
    },
    convertDate(pickerDate) {
      let date = pickerDate.split('/');
      return new Date(date[2], months.indexOf(date[1]), date[0]);
    },
    async toPeiDetail() {
      let hasSignedPeiFromLastYear = await this.hasSignedPei(
        this.studentSieId,
        this.RealAcademicYear - 1
      );

      //llevar directamente a paso 3 de detall si  es ps y al preguntas adicionales si es pei
      let currentstep = 3;
      if (this.PeiGeneralDB.unilateralFlag != '1' && hasSignedPeiFromLastYear) {
        currentstep = 1;
      } else if (
        !hasSignedPeiFromLastYear &&
        this.PeiGeneralDB.unilateralFlag != '1'
      ) {
        currentstep = 2;
      } else {
        currentstep = 3;
      }

      if (this.peiId) {
        for (let i = 0; i < currentstep; i++) {
          this.currentStep.active = false;
          this.currentStep.completed = true;
          this.currentStepIndex++;
          this.currentStep.active = true;
        }
        //set solo preguntas a mostrar
        let questionsToShow = [5.1, 5.2, 5.3, 6, 7.1];

        let step3 = this.getStepElement(4);

        step3.questions.forEach((question) => {
          let questionElement = this.getQuestionElement(4, question.number);
          if (this.PeiGeneralDB.unilateralFlag == '1') {
            if (questionsToShow.includes(questionElement.number)) {
              questionElement.isVisible = true;
            } else {
              questionElement.isVisible = false;
            }
          } else {
            if (questionElement.number == 7.1)
              questionElement.isVisible = false;
          }
        });
      }
    },
    getCurrentYear() {
      return new Date().getFullYear();
    },
    toWizardLastStep(isCopy = false) {
      if (isCopy) {
        this.getStep(
          5
        ).confirmationTitle = `Se comenzará a crear la copia de los datos ingresados en el PEI o PS ${this.signedPeiId}`;
      }

      //Llevar directamente a paso 4 de creación
      for (let i = 0; i < 3; i++) {
        this.currentStep.active = false;
        this.currentStep.completed = true;
        this.currentStepIndex++;
        this.currentStep.active = true;
      }
    },
    toWizardFirstStep() {
      this.getStep(5).confirmationTitle =
        'El PEI se comenzará a crear con la información detallada en las pantallas anteriores.';
      //Llevar directamente a paso 1 de creación
      for (let i = 4; i > 0; i--) {
        this.currentStep.active = false;
        this.currentStep.completed = false;
        this.currentStepIndex--;
        this.currentStep.active = true;
      }
    },
    async candefinepeiacademicyear(studentSieId, academicYearId) {
      let route = `/api/pei/candefinepeiacademicyear?studentSieId=${studentSieId}&academicYearId=${academicYearId}`;
      let self = this;
      let canDefine = false;
      await self
        .$ApiGet(route)
        .then((res) => {
          canDefine = res.data;
        })
        .catch((err) => {
          self.showErrorMessage(err);
        });

      return canDefine;
    },

    async codeReadingLevel() {
      let data = await this.$ApiGet('/api/pei/codeReadingLevel');
      return this.resolveOptions(
        data.data.map((t) => ({
          value: t.id,
          option: t.name,
        }))
      );
    },
    async getSignLanguageProficiencyLevels() {
      let data = await this.$ApiGet(
        '/api/pei/codeSignLanguageProficiencyLevel'
      );
      return this.resolveOptions(
        data.data.map((t) => ({
          value: t.id,
          option: t.name,
        }))
      );
    },
    async codeReadingMode() {
      let data = await this.$ApiGet('/api/pei/codeReadingMode');
      return this.resolveOptions(
        data.data.map((t) => ({
          value: t.id,
          option: t.name,
        }))
      );
    },
    onQuestionP7ValueChange(question) {
      this.showElegibilityDeterminationConditions = question.selectedAnswer;
    },
    async getConditionsCatalog() {
      await studentTrianualService
        .getEligibilityDeterminationConditions()
        .then((res) => {
          this.determinationElegibilityOptions = res.data;
        });
    },
  },
};
</script>

<style scoped lang="scss">
#widzard-header {
  min-height: 150px;
  background-color: white;
  width: 100%;
  border-bottom: 1px solid #cccccc;
}

#div-title {
  font-size: 4em;
}

#btn-cancell {
  top: 997px;
  left: 1143px;
  width: 124px;
  height: 38px;
  background: #c9545e 0% 0% no-repeat padding-box;
  opacity: 1;
  color: white;
}
#currentStep {
  font-size: 1.2em;
}

.wizard {
  height: calc(100vh - 166px);
}

.navbar-container {
  width: 100%;
  margin-top: 10px;
}

.step-pills {
  width: 100%;
}

.nav-tabs > li {
  width: 20%;
}
.step-pills > li {
  width: 20%;
}

#progress-bar {
  height: 3px;
  background: #2db782;
  width: 20%;
}

#section-tabs {
  background: #f2f2f2 0% 0% no-repeat padding-box;
  /* background-image: linear-gradient(rgba(206, 206, 206, 0.75), rgba(255, 255, 255, 0.74)); */
  padding: 15px 25px 25px;
  height: calc(100%);
}
.tab-pane-header {
  padding: 10px 70px;
  box-shadow: 0 10px 30px -12px rgba(224, 224, 224, 1),
    0 4px 25px 0px rgba(224, 224, 224, 1),
    0 8px 10px -5px rgba(224, 224, 224, 1);
}
.tag-badge {
  display: inline-flex;
  padding-right: 1em;
  height: 42px;
  background: transparent linear-gradient(90deg, #ffffff 0%, #e8e8e8 100%) 0% 0%
    no-repeat padding-box;
  border-radius: 26px;
  cursor: pointer;
}

.dot {
  width: 33px;
  height: 33px;
  line-height: 33px;
  border-radius: 50%;
  display: inline-block;
  margin-top: 2px;
  background: #ffffff 0% 0% no-repeat padding-box;
  box-shadow: 0px 3px 6px #00000029;
}
.orange-icon {
  color: #ff7058;
}

.tag-badge-title {
  text-align: left;
  font: Bold 25px/30px Lato;
  letter-spacing: 0;
  color: #11291b;
  display: inline-block;
}

.orange-icon-define {
  color: #ff7058;
  font-size: 25px;
}

.label-current {
  border-radius: 26px;
  background: transparent linear-gradient(90deg, #e2e2e2 0%, #ffffff00 100%) 0%
    0% no-repeat padding-box;
  text-align: left;
  font: 900 20px Lato;
  color: #11291b;
  padding: 5px 14px;
  font-weight: 400;
  float: right;
}

.tab-content {
  box-shadow: 0 10px 30px -12px rgba(0, 0, 0, 0.42),
    0 4px 25px 0px rgba(0, 0, 0, 0.12), 0 8px 10px -5px rgba(0, 0, 0, 0.2);
  height: calc(100vh - 200px);
  overflow-x: auto;
}

.tab-pane-body {
  padding: 25px 40px;
  height: calc(100vh - 285px);
  overflow-y: auto;
}

.error-val {
  color: #ff5b5c;
  display: block;
}
.success-val {
  color: #2db782;
  display: block;
}
.warning-val {
  color: #af6708;
  display: block;
}

.footer-container {
  position: relative;
  bottom: 0;
  width: 100%;
  height: 60px; /* Set the fixed height of the footer here */
  line-height: 60px; /* Vertically center the text there */
  background-color: #f5f5f5;
}

.footer-menu {
  background: #ffffff 0% 0% no-repeat padding-box;
  height: 65px;
  box-shadow: 0 10px 30px -12px rgba(0, 0, 0, 0.42),
    0 4px 25px 0px rgba(0, 0, 0, 0.12), 0 8px 10px -5px rgba(0, 0, 0, 0.2);
}

.footer-row {
  padding-top: 15px;
  padding-right: 25px;
  height: 45px;
}

.btn-footer {
  text-align: center;
  font: 500 16px/29px Lato;
  letter-spacing: 0;
  color: #ffffff;
  height: 38px;
  width: 130px;
  box-shadow: none;
  line-height: 20px;
}
.btn-footer.green {
  color: #2db782;
}

.green:hover,
.green:hover:active {
  background-color: #2db782;
  color: #fff;
  border-color: #2db782;
}

.btn-footer:hover {
  color: white;
  opacity: 0.9;
}
.btn-back {
  background: #c9545e;
}
.btn-next {
  background: #2db782;
}

.custom-red-alert {
  background: #ffebeb 0% 0% no-repeat padding-box;
  border: 1px solid #ff5b5c;
  border-radius: 5px;
  font: 400 16px/24px Lato;
  letter-spacing: 0;
  color: #ff5b5c;
}

.custom-success-alert {
  color: #2db782;
  border: 1px solid #39da8a;
  border-radius: 4px;
}

.custom-warning-alert {
  background: #fff5e8 0% 0% no-repeat padding-box;
  border: 1px solid #fdac41;
  color: #af6708;
}

.action-button-custom {
  display: flex;
  justify-content: center;
  align-items: center;
  word-break: normal !important;
}

@media only screen and (max-width: 768px) {
  @import '@/assets/scss/variables/_colors.scss';
  $titleFont: 9pt;
  .wizard {
    height: calc(100vh - 128px);
    .navbar-container {
      margin-top: 0px;
      .step-pills {
        padding-left: 0px !important;
      }
    }
  }
  ::v-deep .navbar-container {
    border-bottom: 1px solid $gray_5;
    .step {
      .step-label {
        display: none;
      }
    }
  }

  ::v-deep #section-tabs {
    padding: unset;
    background: unset;
    height: 100%;
    .tab-content {
      box-shadow: unset;
      height: 100%;
      border: unset;
      .tab-pane-header {
        padding: 10px 0px;
        border-bottom: 1px solid $gray_5;
        box-shadow: none;
        margin: 0px 11px;
        .tag-badge {
          height: 24px;
          .dot {
            margin-top: -1px;
            width: 23px;
            height: 23px;
            line-height: 15px;
            .orange-icon-define {
              font-size: 17px;
            }
          }
        }
        .tag-badge-title {
          font-size: $titleFont;
          margin-top: -6px;
        }
        .label-current {
          background: unset !important;
          font-size: $titleFont;
          margin-top: -2px;
          margin-right: -13px;
        }
      }
      .tab-pane-body {
        padding: 0px 10px;
        height: 100%;
        width: 100%;
        .container {
          height: 100%;
          .featureIcon {
            i {
              font-size: 0.8em;
            }
          }
          .confirmationTitle {
            font-size: 14pt;
            line-height: 25px;
            flex: unset !important;
            width: 100% !important;
          }
          .confirmationMessage {
            font-size: 14pt;
          }
        }
        #errors-1 {
          margin-top: 10px;
          i {
            font-size: 12px;
          }
          span {
            font-size: 10pt;
          }
        }
        .flex-column {
          flex-direction: unset !important;
        }
        .question-element {
          width: 100%;
          .item-pane {
            box-shadow: none;
            .question-box-content {
              flex-direction: column !important;
              .custom-radio-container {
                width: 100%;
                justify-content: flex-end;
                margin-right: 0px;
                padding-top: 0px !important;
              }
              .question-answer {
                margin-bottom: 10px;
              }
            }
          }
          .item-pane-info {
            p {
              font-size: 10pt;
            }
            .panel-info-description {
              margin-bottom: 20px;
            }
          }
          .question-box {
            padding: 22px 12px;
            .question-title {
              display: block !important;
            }
          }
          .question-line {
            flex: 1;
            p {
              padding-left: 0px !important;
              font-size: 10pt;
            }
            .student-search-alert-error {
              background-color: unset;
              border: unset;
              padding: 0px;
              margin: 0px;
              em {
                font-size: 12px;
              }
              p {
                font-size: 10pt;
              }
            }
            .question-textarea {
              span {
                color: $gray_3;
                font-size: 10pt;
                float: right;
              }
            }
            .question-field-title {
              padding-left: 0px !important;
            }
            .question-field-content {
              padding-left: 0px !important;
              margin-bottom: 20px;
              .field-label {
                p {
                  margin-bottom: 5px;
                }
              }
              .file-input-container {
                span {
                  font-size: 12pt;
                }
              }
              .question-dropdown {
                margin-bottom: 20px;
              }
            }
            ::v-deep .vdp-datepicker__calendar {
              zoom: 0.8;
            }
            ::v-deep .custom-datetime-picker-container {
              margin-bottom: 20px;
            }
          }
          .question-select {
            margin-bottom: 10px !important;
          }
          .dropdown-selector-indicator {
            display: block !important;
          }
          .custom-radio-button {
            height: 55px !important;
            width: 58px !important;
            border-top-left-radius: 10px !important;
            border-top-right-radius: 10px !important;
          }
          .bg-green {
            background: $green_1 !important;
          }
        }
      }
    }
  }

  .action-button-container {
    div:first-child {
      margin-right: 0px !important;
      justify-content: unset !important;
      width: 100%;
    }
    &__action {
      display: flex;
      margin-left: 0px !important;
      .col:first-child {
        margin-right: 1px !important;
      }
      .col:last-child {
        margin-left: 1px !important;
      }
      .col {
        flex: 1;
        padding-left: 0px !important;
        padding-right: 0px !important;
        ::v-deep button {
          border-radius: 0px !important;
          width: 100% !important;
          height: 50px !important;
          i {
            font-size: 20px !important;
            margin-left: -5px !important;
          }
        }
      }
    }
  }
  ::v-deep .swal2-container {
    .custom-cancel-popup {
      width: auto !important;
    }
  }
}
</style>
